/**
 * InteractiveSliceWidget
 * Features:
 * - Visual plane representation with semi-transparent display
 * - Interactive manipulation with TransformControls (translate + rotate)
 * - Real-time slice updates as plane is moved/rotated
 * - Arrow showing normal direction
 * - Automatic positioning at mesh center
 */
class InteractiveSliceWidget {
  constructor(viewer, filterManager, config = {}) {
    this.viewer = viewer;
    this.filterManager = filterManager;
    this.renderer = viewer.engine;
    this.scene = viewer.engine.scene;
    this.camera = viewer.engine.camera;
    this.controls = viewer.engine.controls;

    // Configuration
    this.config = {
      inputPrefix: config.inputPrefix || 'slice',  // 'slice' or 'streamline'
      enableSliceUpdates: config.enableSliceUpdates !== false,  // Whether to compute slices
      checkboxId: config.checkboxId || 'sliceFilterEnabled',  // ID of enable checkbox
      onPlaneChange: config.onPlaneChange || null,  // Callback when plane changes
      planeChangeDebounce: config.planeChangeDebounce || 1000,  // ms to wait before calling onPlaneChange
      hideGizmo: config.hideGizmo || false,  // Hide the transform controls gizmo (arrows/spheres)
      ...config
    };

    // Debounce timer for onPlaneChange callback
    this.planeChangeDebounceTimer = null;
    this.suppressChangeNotification = false;  // Flag to suppress notifications during enable/disable

    // Widget state
    this.enabled = false;
    this.planeControlsVisible = true;  // Track visibility of plane controls (helper + gizmo)
    this.planeHelper = null;
    this.transformControls = null;
    this.normalArrow = null;
    this.planeGroup = null;
    this.currentSliceMesh = null;

    // Plane parameters
    this.planeOrigin = new THREE.Vector3(0, 0, 0);
    this.planeNormal = new THREE.Vector3(0, 0, 1);
    this.planeWidth = 1.0;
    this.planeHeight = 1.0;

    // Settings
    this.settings = {
      planeColor: config.planeColor || 0x4A90E2,
      planeOpacity: 0.3,
      borderColor: config.borderColor || 0x1A5490,
      borderOpacity: 0.8,
      arrowColor: config.arrowColor || 0xFF6B35,
      arrowLength: 0.5,
      updateThrottle: 50, // ms between updates (for slice contours only)
      overlayDebounce: 800, // ms to wait after plane stops moving before computing overlays
      autoSize: true
    };

    // Throttling and debouncing
    this.lastUpdateTime = 0;
    this.updatePending = false;
    this.overlayDebounceTimer = null;
    this.isComputingOverlays = false;
    this.backendSliceDebounceTimer = null;  // Debounce timer for backend slice updates
    this.isComputingBackendSlice = false;   // Flag to prevent concurrent backend slice computations

    this.init();
  }

  init() {
    // Load TransformControls if not already loaded
    this.loadTransformControls().then(() => {
      this.setupEventListeners();
    });
  }

  async loadTransformControls() {
    // Check if TransformControls is already loaded
    if (typeof THREE.TransformControls !== 'undefined') {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/TransformControls.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  setupEventListeners() {
    // Listen to enable checkbox
    const enableCheckbox = document.getElementById(this.config.checkboxId);
    if (enableCheckbox) {
      enableCheckbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          // For streamline widget, only enable if seed type is "plane"
          if (this.config.inputPrefix === 'streamline') {
            const seedTypeSelect = document.getElementById('streamlineSeedType');
            if (seedTypeSelect && seedTypeSelect.value === 'plane') {
              this.enable();
            } else {
              console.log(`[${this.config.inputPrefix} Widget] Checkbox checked but seed type is not 'plane', skipping enable`);
            }
          } else {
            // For slice widget, enable immediately
            this.enable();
          }
        } else {
          this.disable();
        }
      });

      // For streamline widget, don't auto-enable - let filters.js control it based on seed type
      // For slice widget, auto-enable if checkbox is already checked
      if (enableCheckbox.checked && this.config.inputPrefix !== 'streamline') {
        console.log(`[${this.config.inputPrefix} Widget] Checkbox already checked, auto-enabling widget`);
        // Defer enabling until after the viewer is fully loaded
        setTimeout(() => {
          if (this.viewer.currentData) {
            this.enable();
          } else {
            console.log(`[${this.config.inputPrefix} Widget] No data loaded yet, will enable when data loads`);
          }
        }, 500);
      }
    }

    // Also listen to heatmap "Show Plane in 3D View" checkbox
    const slicePlaneShow = document.getElementById('slicePlaneShow');
    if (slicePlaneShow) {
      slicePlaneShow.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Sync heatmap controls to slice controls FIRST
          this.syncHeatmapToSlice();
          // Enable with custom origin/normal from heatmap
          this.enableWithCustomPlane();
        } else {
          this.disable();
        }
      });
    }

    // Listen to display option checkboxes
    const displayOptions = [
      'sliceShowContours',
      'sliceShowStreamlines',
      'sliceShowVectors',
      'sliceShowIsolines'
    ];

    displayOptions.forEach(id => {
      const checkbox = document.getElementById(id);
      if (checkbox) {
        checkbox.addEventListener('change', () => {
          // Show/hide contour settings when checkbox toggled
          if (id === 'sliceShowContours') {
            const settings = document.getElementById('sliceContourSettings');
            if (settings) {
              settings.style.display = checkbox.checked ? 'block' : 'none';
              // Populate field dropdown when showing contour settings
              if (checkbox.checked) {
                console.log('[sliceShowContours] Checkbox checked, calling populateSliceContourFields');
                this.populateSliceContourFields();
              }
            }

            if (this.enabled && !checkbox.checked) {
              // When unchecking contours, remove the slice mesh
              this.removeSliceContours();
            }

            // Notify view lock manager of visibility change for collaboration sync
            if (this.viewer?.viewLockManager) {
              this.viewer.viewLockManager.onVisualizationChange();
            }
          }

          // Show/hide streamline settings when checkbox toggled
          if (id === 'sliceShowStreamlines') {
            const settings = document.getElementById('sliceStreamlineSettings');
            if (settings) {
              settings.style.display = checkbox.checked ? 'block' : 'none';
              // Populate field dropdowns when showing streamline settings
              if (checkbox.checked) {
                console.log('[sliceShowStreamlines] Checkbox checked, calling populateSliceStreamlineFields');
                console.log('[sliceShowStreamlines] this.viewer:', this.viewer);
                console.log('[sliceShowStreamlines] this.viewer.currentData:', this.viewer?.currentData);
                this.populateSliceStreamlineFields();
              }
            }

            if (this.enabled) {
              if (!checkbox.checked) {
                // When unchecking streamlines, remove them immediately
                this.removeSliceStreamlines();
              }
              // Update seed line preview (will show/hide based on checkbox state)
              this.updateSeedLinePreview();
            }

            // Notify view lock manager of visibility change for collaboration sync
            if (this.viewer?.viewLockManager) {
              this.viewer.viewLockManager.onVisualizationChange();
            }
          }

          // AUTO-APPLY DISABLED for streamlines - Users must click Apply button
          // if (this.enabled) {
          //   this.scheduleSliceUpdate();
          // }
        });
      }
    });

    // Handle vectors checkbox - show/hide settings panel
    const vectorsCheckbox = document.getElementById('sliceShowVectors');
    if (vectorsCheckbox) {
      vectorsCheckbox.addEventListener('change', () => {
        const settings = document.getElementById('sliceVectorSettings');
        if (settings) {
          settings.style.display = vectorsCheckbox.checked ? 'block' : 'none';
          // Populate vector field dropdown when showing settings
          if (vectorsCheckbox.checked) {
            this.populateSliceVectorFields();
          }
        }
        if (!vectorsCheckbox.checked) {
          // Remove vectors when unchecked
          this.removeSliceVectors();
        }

        // Notify view lock manager of visibility change for collaboration sync
        if (this.viewer?.viewLockManager) {
          this.viewer.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Apply Vectors button - only for slice widget, not streamline widget
    const applyVectorsBtn = document.getElementById('btnApplySliceVectors');
    if (applyVectorsBtn && this.config.inputPrefix === 'slice' && !applyVectorsBtn._listenerAttached) {
      applyVectorsBtn._listenerAttached = true;
      applyVectorsBtn.addEventListener('click', async () => {
        await this.runWithProgressBar(
          'btnApplySliceVectors',
          'sliceVectorsProgressBar',
          async () => {
            this.forceVectorsGeneration = true;
            if (this.viewer.renderingMode === 'geometry') {
              await this.updateSliceBackend();
            } else {
              await this.updateSlice();
            }
          },
          {
            startMessage: 'Generating velocity vectors on plane...',
            successMessage: 'Velocity vectors generated successfully',
            errorPrefix: 'Velocity vectors error'
          }
        );
      });
    }

    // Handle isolines checkbox - show/hide settings panel
    const isolinesCheckbox = document.getElementById('sliceShowIsolines');
    if (isolinesCheckbox) {
      isolinesCheckbox.addEventListener('change', () => {
        const settings = document.getElementById('sliceIsolinesSettings');
        if (settings) {
          settings.style.display = isolinesCheckbox.checked ? 'block' : 'none';
          // Populate field dropdown when showing settings
          if (isolinesCheckbox.checked) {
            this.populateSliceIsolinesFields();
          }
        }
        if (!isolinesCheckbox.checked) {
          // Remove isolines when unchecked
          this.removeSliceIsolines();
        }

        // Notify view lock manager of visibility change for collaboration sync
        if (this.viewer?.viewLockManager) {
          this.viewer.viewLockManager.onVisualizationChange();
        }
      });
    }

    // Apply Isolines button - only for slice widget, not streamline widget
    const applyIsolinesBtn = document.getElementById('btnApplySliceIsolines');
    if (applyIsolinesBtn && this.config.inputPrefix === 'slice' && !applyIsolinesBtn._listenerAttached) {
      applyIsolinesBtn._listenerAttached = true;
      applyIsolinesBtn.addEventListener('click', async () => {
        await this.runWithProgressBar(
          'btnApplySliceIsolines',
          'sliceIsolinesProgressBar',
          async () => {
            this.forceIsolinesGeneration = true;
            if (this.viewer.renderingMode === 'geometry') {
              await this.updateSliceBackend();
            } else {
              await this.updateSlice();
            }
          },
          {
            startMessage: 'Generating contour lines on plane...',
            successMessage: 'Contour lines generated successfully',
            errorPrefix: 'Contour lines error'
          }
        );
      });
    }

    // Isolines are now supported in geometry mode

    // Listen to seed count input changes
    // AUTO-APPLY DISABLED for slice streamlines - Users must click Apply button
    const seedCountInput = document.getElementById('sliceStreamlineSeedCount');
    // if (seedCountInput) {
    //   seedCountInput.addEventListener('change', () => {
    //     if (this.enabled) {
    //       this.scheduleSliceUpdate();
    //     }
    //   });
    // }

    const vectorFieldSelect = document.getElementById('sliceStreamlineVectorField');
    // if (vectorFieldSelect) {
    //   vectorFieldSelect.addEventListener('change', () => {
    //     if (this.enabled) {
    //       this.scheduleSliceUpdate();
    //     }
    //   });
    // }

    const colorFieldSelect = document.getElementById('sliceStreamlineColorField');
    // if (colorFieldSelect) {
    //   colorFieldSelect.addEventListener('change', () => {
    //     if (this.enabled) {
    //       this.scheduleSliceUpdate();
    //     }
    //   });
    // }

    const colormapSelect = document.getElementById('sliceStreamlineColormap');
    if (colormapSelect) {
      colormapSelect.addEventListener('change', () => {
        if (this.enabled) {
          // Only update colors locally - don't reload from backend!
          this.updateSliceStreamlineColors();
        }
      });
    }

    // Seed line rotation slider
    const rotationSlider = document.getElementById('sliceStreamlineRotation');
    const rotationValue = document.getElementById('sliceStreamlineRotationValue');
    if (rotationSlider && rotationValue) {
      rotationSlider.addEventListener('input', () => {
        rotationValue.textContent = rotationSlider.value;
        // Update visual seed line preview and start blinking
        // AUTO-APPLY DISABLED - Users must click Apply button
        if (this.enabled) {
          this.updateSeedLinePreview();
          this.startSeedLineBlinking();
          // this.scheduleSliceUpdate(); // DISABLED
        }
      });
    }

    // Seed line offset slider
    const offsetSlider = document.getElementById('sliceStreamlineOffset');
    const offsetValue = document.getElementById('sliceStreamlineOffsetValue');
    if (offsetSlider && offsetValue) {
      offsetSlider.addEventListener('input', () => {
        offsetValue.textContent = offsetSlider.value;
        // Update visual seed line preview and start blinking
        // AUTO-APPLY DISABLED - Users must click Apply button
        if (this.enabled) {
          this.updateSeedLinePreview();
          this.startSeedLineBlinking();
          // this.scheduleSliceUpdate(); // DISABLED
        }
      });
    }

    // Apply button for slice streamlines - manual control
    const btnApplySliceStreamlines = document.getElementById('btnApplySliceStreamlines');
    if (btnApplySliceStreamlines && !btnApplySliceStreamlines._listenerAttached) {
      btnApplySliceStreamlines._listenerAttached = true;
      btnApplySliceStreamlines.addEventListener('click', async () => {
        const streamlinesCheckbox = document.getElementById('sliceShowStreamlines');
        if (!streamlinesCheckbox?.checked) {
          console.warn('[Apply Slice Streamlines] Streamlines checkbox not checked');
          return;
        }

        console.log('[Apply Slice Streamlines] Button clicked, forcing streamlines generation');

        await this.runWithProgressBar(
          'btnApplySliceStreamlines',
          'sliceStreamlinesProgressBar',
          async () => {
            this.forceStreamlinesGeneration = true;
            if (this.viewer.renderingMode === 'geometry') {
              await this.updateSliceBackend();
            } else {
              await this.updateSlice();
            }
          },
          {
            startMessage: 'Generating 2D streamlines on plane...',
            successMessage: '2D streamlines generated successfully',
            errorPrefix: 'Slice streamlines error'
          }
        );
      });
      console.log('[Slice Widget] Apply Slice Streamlines button handler registered');
    }

    // Apply button for slice colored contours - manual control
    const btnApplySliceContours = document.getElementById('btnApplySliceContours');
    if (btnApplySliceContours && !btnApplySliceContours._listenerAttached) {
      btnApplySliceContours._listenerAttached = true;
      btnApplySliceContours.addEventListener('click', async () => {
        const contoursCheckbox = document.getElementById('sliceShowContours');
        if (!contoursCheckbox?.checked) {
          console.warn('[Apply Slice Contours] Contours checkbox not checked');
          return;
        }

        const fieldSelect = document.getElementById('sliceContourField');
        const selectedField = fieldSelect?.value;
        if (!selectedField) {
          Utils.notify('Please select a field for colored contours', 'warning');
          return;
        }

        console.log('[Apply Slice Contours] Button clicked, generating colored contours for field:', selectedField);

        await this.runWithProgressBar(
          'btnApplySliceContours',
          'sliceContoursProgressBar',
          async () => {
            this.forceContoursGeneration = true;
            if (this.viewer.renderingMode === 'geometry') {
              await this.updateSliceBackend();
            } else {
              await this.updateSlice();
            }
          },
          {
            startMessage: 'Generating colored contours on slice...',
            successMessage: 'Colored contours generated successfully',
            errorPrefix: 'Slice contours error'
          }
        );
      });
      console.log('[Slice Widget] Apply Slice Contours button handler registered');
    }

    // Toggle Plane Controls button (hide/show the visual plane helper and transform gizmo)
    const btnTogglePlaneControls = document.getElementById('btnTogglePlaneControls');
    if (btnTogglePlaneControls && !btnTogglePlaneControls._listenerAttached) {
      btnTogglePlaneControls._listenerAttached = true;
      // Track visibility state
      this.planeControlsVisible = true;

      btnTogglePlaneControls.addEventListener('click', () => {
        this.planeControlsVisible = !this.planeControlsVisible;
        this.setPlaneControlsVisibility(this.planeControlsVisible);

        // Update button text and icon
        const textSpan = document.getElementById('planeControlsText');
        const iconSvg = document.getElementById('planeControlsIcon');

        if (textSpan) {
          textSpan.textContent = this.planeControlsVisible ? 'Hide Plane Controls' : 'Show Plane Controls';
        }

        if (iconSvg) {
          if (this.planeControlsVisible) {
            // Eye open icon
            iconSvg.innerHTML = `
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            `;
          } else {
            // Eye closed icon
            iconSvg.innerHTML = `
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            `;
          }
        }

        console.log('[Slice Widget] Plane controls visibility:', this.planeControlsVisible);
      });
      console.log('[Slice Widget] Toggle Plane Controls button handler registered');
    }

    // Listen to manual input changes (for synchronization)
    const prefix = this.config.inputPrefix;

    // Only listen to origin/normal inputs for slice widget (not streamline widget)
    // For streamline widget, position/orientation is controlled purely via drag-and-drop
    if (prefix === 'slice') {
      // Listen to plane inputs
      const planeOriginInputs = [`${prefix}PlaneOriginX`, `${prefix}PlaneOriginY`, `${prefix}PlaneOriginZ`];
      const planeNormalInputs = [`${prefix}PlaneNormalX`, `${prefix}PlaneNormalY`, `${prefix}PlaneNormalZ`];

      planeOriginInputs.forEach((id, index) => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('change', () => {
            if (this.enabled && !this.isUpdatingInputs) {
              this.updatePlaneFromInputs();
            }
          });
        }
      });

      planeNormalInputs.forEach((id, index) => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('change', () => {
            if (this.enabled && !this.isUpdatingInputs) {
              this.updatePlaneFromInputs();
            }
          });
        }
      });

      // Also listen to legacy slice filter inputs
      const originInputs = ['sliceOriginX', 'sliceOriginY', 'sliceOriginZ'];
      const normalInputs = ['sliceNormalX', 'sliceNormalY', 'sliceNormalZ'];

      originInputs.forEach((id, index) => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('change', () => {
            if (this.enabled && !this.isUpdatingInputs) {
              this.updatePlaneFromInputs();
            }
          });
        }
      });

      normalInputs.forEach((id, index) => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('change', () => {
            if (this.enabled && !this.isUpdatingInputs) {
              this.updatePlaneFromInputs();
            }
          });
        }
      });
    }

    // Listen to width/height input changes
    const widthInput = document.getElementById(`${prefix}PlaneWidth`);
    const heightInput = document.getElementById(`${prefix}PlaneHeight`);

    const updateSize = () => {
      console.log(`[${prefix} Widget] Width/height input changed. Enabled: ${this.enabled}, Width: ${widthInput?.value}, Height: ${heightInput?.value}`);
      if (this.enabled) {
        console.log(`[${prefix} Widget] Widget is enabled, updating plane size`);
        this.updatePlaneSizeFromInputs();
      } else {
        console.warn(`[${prefix} Widget] Widget is NOT enabled - cannot update size. Enable the widget first (check the checkbox).`);
      }
    };

    if (widthInput) {
      console.log(`[${prefix} Widget] Attaching event listeners to ${prefix}PlaneWidth`);
      widthInput.addEventListener('change', updateSize);
      widthInput.addEventListener('blur', updateSize);  // Also trigger on blur
    } else {
      console.warn(`[${prefix} Widget] Could not find input: ${prefix}PlaneWidth`);
    }

    if (heightInput) {
      console.log(`[${prefix} Widget] Attaching event listeners to ${prefix}PlaneHeight`);
      heightInput.addEventListener('change', updateSize);
      heightInput.addEventListener('blur', updateSize);  // Also trigger on blur
    } else {
      console.warn(`[${prefix} Widget] Could not find input: ${prefix}PlaneHeight`);
    }

    console.log(`[${prefix} Widget] Width/Height event listeners registered. Inputs found: width=${!!widthInput}, height=${!!heightInput}`);
  }

  enable() {
    if (this.enabled) return;

    const prefix = this.config.inputPrefix;
    console.log(`[${prefix} Widget] enable() called - initializing widget`);

    // Suppress change notifications during enable (just showing the widget shouldn't trigger recomputation)
    this.suppressChangeNotification = true;

    const data = this.viewer.currentData;

    // Get bounding box - works for both frontend and backend modes
    let bbox;
    if (this.viewer.renderingMode === 'geometry') {
      // Backend mode - get bounds from the rendered mesh in the scene
      bbox = this.getBoundingBoxFromScene();
      if (!bbox) {
        console.warn(`[${prefix} Widget] No mesh data available for slice widget (backend mode)`);
        this.suppressChangeNotification = false;
        return;
      }
    } else {
      // Frontend mode - get bounds from data.grid
      if (!data || !data.grid) {
        console.warn(`[${prefix} Widget] No mesh data available for slice widget (frontend mode)`);
        this.suppressChangeNotification = false;
        return;
      }
      bbox = this.calculateBoundingBox(data.grid);
    }

    console.log(`[${prefix} Widget] Mesh bounding box:`, {
      min: bbox.min,
      max: bbox.max,
      center: bbox.center,
      size: bbox.size
    });

    this.enabled = true;

    const size = bbox.size;

    // Choose normal perpendicular to SMALLEST dimension (not largest)
    // This ensures the plane cuts through the most mesh volume
    if (size.x <= size.y && size.x <= size.z) {
      this.planeNormal.set(1, 0, 0);  // X-normal (cuts through YZ, smallest X span)
      this.planeOrigin.copy(bbox.center);
      console.log('Using X-normal plane (X is smallest dimension)');
    } else if (size.y <= size.x && size.y <= size.z) {
      this.planeNormal.set(0, 1, 0);  // Y-normal (cuts through XZ, smallest Y span)
      this.planeOrigin.copy(bbox.center);
      console.log('Using Y-normal plane (Y is smallest dimension)');
    } else {
      this.planeNormal.set(0, 0, 1);  // Z-normal (cuts through XY, smallest Z span)
      this.planeOrigin.copy(bbox.center);
      console.log('Using Z-normal plane (Z is smallest dimension)');
    }

    console.log(`Mesh bounds: X=[${bbox.min.x.toFixed(2)}, ${bbox.max.x.toFixed(2)}], Y=[${bbox.min.y.toFixed(2)}, ${bbox.max.y.toFixed(2)}], Z=[${bbox.min.z.toFixed(2)}, ${bbox.max.z.toFixed(2)}]`);
    console.log(`Plane origin: ${this.planeOrigin.toArray().map(v => v.toFixed(2))}, normal: ${this.planeNormal.toArray()}`);

    // Calculate appropriate plane size based on the dimensions the plane spans
    this.calculatePlaneSize(bbox);

    // Create visual widgets
    this.createPlaneHelper();
    // Don't create normal arrow - too much visual clutter
    // this.createNormalArrow();
    this.createTransformControls();

    // Update input fields
    this.updateInputFields();

    // Create initial slice (only for slice widget, not streamline widget)
    if (this.config.enableSliceUpdates) {
      this.updateSlice();
    }

    // Populate field dropdowns if streamlines are enabled
    const streamlinesCheckbox = document.getElementById('sliceShowStreamlines');
    if (streamlinesCheckbox && streamlinesCheckbox.checked) {
      this.populateSliceStreamlineFields();
    }

    console.log(`[${prefix} Widget] ✅ Widget enabled successfully. Final state:`, {
      origin: this.planeOrigin.toArray(),
      normal: this.planeNormal.toArray(),
      width: this.planeWidth,
      height: this.planeHeight,
      inputPrefix: prefix,
      checkboxId: this.config.checkboxId
    });

    // Reset plane controls visibility button to default state
    this.planeControlsVisible = true;
    const textSpan = document.getElementById('planeControlsText');
    const iconSvg = document.getElementById('planeControlsIcon');
    if (textSpan) textSpan.textContent = 'Hide Plane Controls';
    if (iconSvg) {
      iconSvg.innerHTML = `
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      `;
    }

    // Re-enable change notifications now that widget is fully initialized
    this.suppressChangeNotification = false;
  }

  /**
   * Enable widget with custom plane position (from heatmap controls)
   * Instead of auto-calculating plane position, use the values already in the inputs
   */
  enableWithCustomPlane() {
    console.log('[Slice Widget] enableWithCustomPlane() called');

    if (this.enabled) {
      console.log('[Slice Widget] Already enabled, skipping');
      return;
    }

    // Suppress change notifications during enable (just showing the widget shouldn't trigger recomputation)
    this.suppressChangeNotification = true;

    const data = this.viewer.currentData;

    // Get bounding box - works for both frontend and backend modes
    let bbox;
    if (this.viewer.renderingMode === 'geometry') {
      console.log('[Slice Widget] Backend mode, getting bounds from scene');
      // Backend mode - get bounds from the rendered mesh in the scene
      bbox = this.getBoundingBoxFromScene();
      if (!bbox) {
        console.warn('[Slice Widget] No mesh data available for slice widget (backend mode)');
        this.suppressChangeNotification = false;
        return;
      }
    } else {
      console.log('[Slice Widget] Frontend mode, getting bounds from data');
      // Frontend mode - get bounds from data.grid
      if (!data || !data.grid) {
        console.warn('[Slice Widget] No mesh data available for slice widget (frontend mode)');
        this.suppressChangeNotification = false;
        return;
      }
      bbox = this.calculateBoundingBox(data.grid);
    }

    this.enabled = true;

    // If origin is at (0,0,0) default, auto-center on mesh
    const originMagnitude = this.planeOrigin.length();
    if (originMagnitude < 0.001) {
      console.log('[Slice Widget] Origin at default (0,0,0), auto-centering on mesh');
      this.planeOrigin.copy(bbox.center);
      // Update hidden inputs
      document.getElementById('slicePlaneOriginX').value = bbox.center.x.toFixed(3);
      document.getElementById('slicePlaneOriginY').value = bbox.center.y.toFixed(3);
      document.getElementById('slicePlaneOriginZ').value = bbox.center.z.toFixed(3);
    }

    console.log(`[Slice Widget] Using plane: origin=${this.planeOrigin.toArray().map(v => v.toFixed(2))}, normal=${this.planeNormal.toArray().map(v => v.toFixed(3))}`);

    // Calculate appropriate plane size based on the dimensions the plane spans
    this.calculatePlaneSize(bbox);

    // Create visual widgets
    console.log('[Slice Widget] Creating plane helper...');
    this.createPlaneHelper();
    // Don't create normal arrow - too much visual clutter
    // console.log('[Slice Widget] Creating normal arrow...');
    // this.createNormalArrow();
    console.log('[Slice Widget] Creating transform controls...');
    this.createTransformControls();

    // Update input fields (will sync both slice and heatmap inputs)
    this.updateInputFields();

    // Create initial slice (only for slice widget, not streamline widget)
    if (this.config.enableSliceUpdates) {
      console.log('[Slice Widget] Updating slice...');
      this.updateSlice();
    }

    // Populate field dropdowns if streamlines are enabled
    const streamlinesCheckbox = document.getElementById('sliceShowStreamlines');
    if (streamlinesCheckbox && streamlinesCheckbox.checked) {
      this.populateSliceStreamlineFields();
    }

    console.log('[Slice Widget] ✓ Interactive slice widget enabled with custom plane position');

    // Re-enable change notifications now that widget is fully initialized
    this.suppressChangeNotification = false;
  }

  disable() {
    if (!this.enabled) return;

    this.enabled = false;

    // Clear any pending timers
    if (this.backendSliceDebounceTimer) {
      clearTimeout(this.backendSliceDebounceTimer);
      this.backendSliceDebounceTimer = null;
    }
    if (this.overlayDebounceTimer) {
      clearTimeout(this.overlayDebounceTimer);
      this.overlayDebounceTimer = null;
    }

    // Stop seed line blinking
    this.stopSeedLineBlinking();

    // Remove all visual elements
    this.removeTransformControls();
    this.removePlaneHelper();
    this.removeNormalArrow();
    this.removeSlice();

    // Remove seed line preview (use prefix to avoid conflicts)
    if (this.seedLinePreview) {
      const prefix = this.config.inputPrefix;
      const seedLinePreviewName = `${prefix}-seed-line-preview`;
      this.renderer.removeObject(seedLinePreviewName);
      this.seedLinePreview = null;
      this.seedLineIndicator = null;
    }

    console.log('Interactive slice widget disabled');

    // Broadcast state change to sync with followers
    if (window.viewer?.viewLockManager) {
      window.viewer.viewLockManager.onVisualizationChange(true);
    }
  }

  /**
   * Set visibility of plane controls (plane helper and transform gizmo)
   * without affecting the slice visualization (contours, streamlines, etc.)
   */
  setPlaneControlsVisibility(visible) {
    const prefix = this.config.inputPrefix;
    console.log(`[Slice Widget] setPlaneControlsVisibility(${visible}) called`);

    // Hide/show the plane helper (the visual plane mesh - NOT the contours!)
    if (this.planeGroup) {
      console.log(`[Slice Widget] Setting planeGroup.visible = ${visible}`);
      this.planeGroup.visible = visible;
    }

    // Hide/show the transform controls (gizmo)
    if (this.transformControls) {
      console.log(`[Slice Widget] Setting transformControls.visible = ${visible}`);
      this.transformControls.visible = visible;
      this.transformControls.enabled = visible;
    }

    // Hide/show the normal arrow if it exists
    if (this.normalArrow) {
      this.normalArrow.visible = visible;
    }

    // Hide/show seed line preview if it exists
    if (this.seedLinePreview) {
      this.seedLinePreview.visible = visible;
    }
    if (this.seedLineIndicator) {
      this.seedLineIndicator.visible = visible;
    }

    // Make sure the slice contours mesh (if any) is NOT affected
    // The contours mesh is stored in the renderer with name `${prefix}-plane`
    const slicePlaneObjectName = `${prefix}-plane`;
    const sliceContoursEntry = this.renderer.objects?.get(slicePlaneObjectName);
    if (sliceContoursEntry?.object) {
      // Ensure the contours mesh visibility is controlled by the checkbox, not this toggle
      const contoursCheckbox = document.getElementById('sliceShowContours');
      const shouldShowContours = contoursCheckbox?.checked ?? true;
      console.log(`[Slice Widget] Ensuring contours mesh (${slicePlaneObjectName}) visibility = ${shouldShowContours}`);
      sliceContoursEntry.object.visible = shouldShowContours;
    }

    // Request render to update the view
    if (this.renderer && this.renderer.render) {
      this.renderer.render();
    }

    console.log(`[Slice Widget] Plane controls visibility set to: ${visible}`);
  }

  calculateBoundingBox(grid) {
    const vertices = grid.vertices;
    const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      min.x = Math.min(min.x, v[0]);
      min.y = Math.min(min.y, v[1]);
      min.z = Math.min(min.z, v[2]);
      max.x = Math.max(max.x, v[0]);
      max.y = Math.max(max.y, v[1]);
      max.z = Math.max(max.z, v[2]);
    }

    const center = new THREE.Vector3(
      (min.x + max.x) / 2,
      (min.y + max.y) / 2,
      (min.z + max.z) / 2
    );

    const size = new THREE.Vector3(
      max.x - min.x,
      max.y - min.y,
      max.z - min.z
    );

    return { min, max, center, size };
  }

  getBoundingBoxFromScene() {
    // Get bounding box from the rendered mesh in the scene (backend mode)
    const meshObject = this.renderer.objects.get('main-mesh');
    if (!meshObject || !meshObject.object) {
      console.warn('[Slice Widget] No main-mesh found in scene');
      return null;
    }

    const obj = meshObject.object;

    // Handle both single mesh and nested groups
    let box;
    if (obj.type === 'Group') {
      // Compute bounding box from all meshes in the group (including nested groups)
      box = new THREE.Box3();
      obj.traverse((child) => {
        if (child.isMesh && child.geometry) {
          if (!child.geometry.boundingBox) {
            child.geometry.computeBoundingBox();
          }
          // Expand the box to include this mesh's bounding box
          const meshBox = child.geometry.boundingBox.clone();
          meshBox.applyMatrix4(child.matrixWorld);
          box.union(meshBox);
        }
      });
    } else if (obj.isMesh && obj.geometry) {
      // Single mesh
      if (!obj.geometry.boundingBox) {
        obj.geometry.computeBoundingBox();
      }
      box = obj.geometry.boundingBox.clone();
    } else {
      console.warn('[Slice Widget] Object is neither a mesh nor a group with meshes');
      return null;
    }

    const min = box.min.clone();
    const max = box.max.clone();

    const center = new THREE.Vector3(
      (min.x + max.x) / 2,
      (min.y + max.y) / 2,
      (min.z + max.z) / 2
    );

    const size = new THREE.Vector3(
      max.x - min.x,
      max.y - min.y,
      max.z - min.z
    );

    console.log('[Slice Widget] Computed bounding box from scene:', { min, max, center, size });
    return { min, max, center, size };
  }

  createPlaneHelper() {
    // Create a group to hold plane and border
    this.planeGroup = new THREE.Group();

    // Create semi-transparent plane with separate width and height
    const planeGeometry = new THREE.PlaneGeometry(this.planeWidth, this.planeHeight);
    const planeMaterial = new THREE.MeshBasicMaterial({
      color: this.settings.planeColor,
      transparent: true,
      opacity: this.settings.planeOpacity,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    this.planeHelper = new THREE.Mesh(planeGeometry, planeMaterial);
    this.planeGroup.add(this.planeHelper);

    // Create border/outline
    const borderGeometry = new THREE.EdgesGeometry(planeGeometry);
    const borderMaterial = new THREE.LineBasicMaterial({
      color: this.settings.borderColor,
      transparent: true,
      opacity: this.settings.borderOpacity,
      linewidth: 2
    });

    const border = new THREE.LineSegments(borderGeometry, borderMaterial);
    this.planeGroup.add(border);

    // Position and orient the plane
    this.updatePlaneTransform();

    // Add to scene
    this.scene.add(this.planeGroup);
  }

  createNormalArrow() {
    const origin = this.planeOrigin.clone();
    const direction = this.planeNormal.clone();
    const length = this.planeSize * this.settings.arrowLength;

    this.normalArrow = new THREE.ArrowHelper(
      direction,
      origin,
      length,
      this.settings.arrowColor,
      length * 0.2,
      length * 0.15
    );

    // Make arrow render on top
    this.normalArrow.traverse((child) => {
      if (child.material) {
        child.material.depthTest = false;
        child.renderOrder = 999;
      }
    });

    this.scene.add(this.normalArrow);
  }

  createTransformControls() {
    if (!THREE.TransformControls) {
      console.error('TransformControls not loaded');
      return;
    }

    if (!this.planeGroup) {
      console.warn('Slice widget: plane group not initialized before transform controls');
      this.createPlaneHelper();
      this.updatePlaneTransform();
      if (!this.planeGroup) {
        console.error('Slice widget: unable to initialize plane group for transform controls');
        return;
      }
    }

    this.transformControls = new THREE.TransformControls(
      this.camera,
      this.renderer.renderer.domElement
    );

    // Start with translate mode
    this.transformControls.setMode('translate');

    // Set to local space so axes align with the plane orientation
    this.transformControls.setSpace('local');

    // Attach to plane group
    if (this.planeGroup) {
      this.transformControls.attach(this.planeGroup);
    }
    this.scene.add(this.transformControls);

    // Hide gizmo if configured (plane still responds to drag, but no visual handles)
    if (this.config.hideGizmo) {
      this.transformControls.visible = false;
      // Make the plane group itself respond to pointer events
      if (this.planeGroup) {
        this.planeGroup.userData.draggable = true;
      }
    }

    // Event listeners
    this.transformControls.addEventListener('dragging-changed', (event) => {
      // Disable orbit controls while dragging
      this.controls.enabled = !event.value;
    });

    this.transformControls.addEventListener('change', () => {
      this.onTransformChange();
    });

    // Add keyboard shortcuts for mode switching
    this.keydownHandler = (event) => {
      if (!this.enabled) return;

      switch (event.key.toLowerCase()) {
        case 'g': // Translate (like Blender)
          this.transformControls.setMode('translate');
          console.log('Slice widget: Translate mode');
          break;
        case 'r': // Rotate
          this.transformControls.setMode('rotate');
          console.log('Slice widget: Rotate mode');
          break;
        case 'escape':
          this.transformControls.detach();
          break;
      }
    };

    window.addEventListener('keydown', this.keydownHandler);
  }

  onTransformChange() {
    if (!this.enabled || !this.planeGroup) {
      return;
    }

    // Update plane origin from group position
    this.planeOrigin.copy(this.planeGroup.position);

    // Extract normal from plane rotation
    const normalLocal = new THREE.Vector3(0, 0, 1);
    normalLocal.applyQuaternion(this.planeGroup.quaternion);
    this.planeNormal.copy(normalLocal).normalize();

    // Update arrow
    this.updateNormalArrow();

    // Update input fields
    this.updateInputFields();

    // Update seed line preview if streamlines are enabled
    this.updateSeedLinePreview();

    // Throttle slice updates for performance
    this.scheduleSliceUpdate();

    // Notify external listeners (e.g., for streamline recomputation)
    this.notifyPlaneChange();
  }

  updatePlaneFromInputs() {
    const prefix = this.config.inputPrefix;

    // Read from configurable inputs (e.g., 'slicePlaneOriginX' or 'streamlinePlaneOriginX')
    const origin = new THREE.Vector3(
      parseFloat(document.getElementById(`${prefix}PlaneOriginX`).value) || 0,
      parseFloat(document.getElementById(`${prefix}PlaneOriginY`).value) || 0,
      parseFloat(document.getElementById(`${prefix}PlaneOriginZ`).value) || 0
    );

    const normal = new THREE.Vector3(
      parseFloat(document.getElementById(`${prefix}PlaneNormalX`).value) || 0,
      parseFloat(document.getElementById(`${prefix}PlaneNormalY`).value) || 0,
      parseFloat(document.getElementById(`${prefix}PlaneNormalZ`).value) || 1
    ).normalize();

    this.planeOrigin.copy(origin);
    this.planeNormal.copy(normal);

    this.updatePlaneTransform();
    this.updateNormalArrow();
    this.updateSeedLinePreview();

    if (this.config.enableSliceUpdates) {
      this.updateSlice();
    }
  }

  updatePlaneTransform() {
    if (!this.planeGroup) return;

    // Set position
    this.planeGroup.position.copy(this.planeOrigin);

    // Set rotation to align with normal
    const quaternion = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 0, 1);
    quaternion.setFromUnitVectors(up, this.planeNormal);
    this.planeGroup.setRotationFromQuaternion(quaternion);
  }

  updateNormalArrow() {
    if (!this.normalArrow) return;

    this.normalArrow.position.copy(this.planeOrigin);
    this.normalArrow.setDirection(this.planeNormal);

    const length = this.planeSize * this.settings.arrowLength;
    this.normalArrow.setLength(length, length * 0.2, length * 0.15);
  }

  updateInputFields() {
    // Prevent recursive updates
    this.isUpdatingInputs = true;

    const prefix = this.config.inputPrefix;

    // Update plane controls based on prefix (e.g., 'slicePlaneOriginX' or 'streamlinePlaneOriginX')
    const originXId = `${prefix}PlaneOriginX`;
    const originYId = `${prefix}PlaneOriginY`;
    const originZId = `${prefix}PlaneOriginZ`;
    const normalXId = `${prefix}PlaneNormalX`;
    const normalYId = `${prefix}PlaneNormalY`;
    const normalZId = `${prefix}PlaneNormalZ`;

    const originXInput = document.getElementById(originXId);
    if (originXInput) {
      originXInput.value = this.planeOrigin.x.toFixed(3);
      document.getElementById(originYId).value = this.planeOrigin.y.toFixed(3);
      document.getElementById(originZId).value = this.planeOrigin.z.toFixed(3);

      document.getElementById(normalXId).value = this.planeNormal.x.toFixed(3);
      document.getElementById(normalYId).value = this.planeNormal.y.toFixed(3);
      document.getElementById(normalZId).value = this.planeNormal.z.toFixed(3);
    }

    // For slice widget, also update the legacy slice filter controls
    if (prefix === 'slice') {
      document.getElementById('sliceOriginX').value = this.planeOrigin.x.toFixed(3);
      document.getElementById('sliceOriginY').value = this.planeOrigin.y.toFixed(3);
      document.getElementById('sliceOriginZ').value = this.planeOrigin.z.toFixed(3);

      document.getElementById('sliceNormalX').value = this.planeNormal.x.toFixed(3);
      document.getElementById('sliceNormalY').value = this.planeNormal.y.toFixed(3);
      document.getElementById('sliceNormalZ').value = this.planeNormal.z.toFixed(3);
    }

    setTimeout(() => {
      this.isUpdatingInputs = false;
    }, 100);
  }

  /**
   * Notify external callback that plane has changed (with debouncing)
   */
  notifyPlaneChange() {
    if (!this.config.onPlaneChange) return;

    // Skip notifications if suppressed (e.g., during enable/disable)
    if (this.suppressChangeNotification) {
      console.log(`[${this.config.inputPrefix} Widget] Change notification suppressed (widget enable/disable)`);
      return;
    }

    // Clear existing timer
    if (this.planeChangeDebounceTimer) {
      clearTimeout(this.planeChangeDebounceTimer);
    }

    // Set new timer
    this.planeChangeDebounceTimer = setTimeout(() => {
      console.log(`[${this.config.inputPrefix} Widget] Plane changed, notifying callback after debounce`);
      this.config.onPlaneChange({
        origin: this.planeOrigin.clone(),
        normal: this.planeNormal.clone(),
        width: this.planeWidth,
        height: this.planeHeight
      });
      this.planeChangeDebounceTimer = null;
    }, this.config.planeChangeDebounce);
  }

  /**
   * Sync heatmap controls to slice filter controls
   */
  syncHeatmapToSlice() {
    console.log('[Slice Widget] syncHeatmapToSlice() called');

    const ox = parseFloat(document.getElementById('slicePlaneOriginX')?.value || 0);
    const oy = parseFloat(document.getElementById('slicePlaneOriginY')?.value || 0);
    const oz = parseFloat(document.getElementById('slicePlaneOriginZ')?.value || 0);

    const nx = parseFloat(document.getElementById('slicePlaneNormalX')?.value || 0);
    const ny = parseFloat(document.getElementById('slicePlaneNormalY')?.value || 0);
    const nz = parseFloat(document.getElementById('slicePlaneNormalZ')?.value || 1);

    console.log(`[Slice Widget] Read from heatmap: origin=[${ox}, ${oy}, ${oz}], normal=[${nx}, ${ny}, ${nz}]`);

    document.getElementById('sliceOriginX').value = ox;
    document.getElementById('sliceOriginY').value = oy;
    document.getElementById('sliceOriginZ').value = oz;

    document.getElementById('sliceNormalX').value = nx;
    document.getElementById('sliceNormalY').value = ny;
    document.getElementById('sliceNormalZ').value = nz;

    // Update widget's internal state
    this.planeOrigin.set(ox, oy, oz);
    this.planeNormal.set(nx, ny, nz).normalize();

    console.log(`[Slice Widget] Synced to widget: origin=${this.planeOrigin.toArray()}, normal=${this.planeNormal.toArray()}`);
  }

  scheduleSliceUpdate() {
    if (!this.config.enableSliceUpdates) return;

    const now = Date.now();

    if (now - this.lastUpdateTime < this.settings.updateThrottle) {
      // Schedule update if not already pending
      if (!this.updatePending) {
        this.updatePending = true;
        setTimeout(() => {
          this.updateSlice();
          this.updatePending = false;
        }, this.settings.updateThrottle);
      }
    } else {
      // Update immediately
      this.updateSlice();
    }
  }

  async updateSlice() {
    if (!this.enabled) return;

    // Skip updates if currently computing overlays to avoid race conditions
    if (this.isComputingOverlays) {
      console.log('⏸️ Skipping slice update (overlays being computed)');
      return;
    }

    this.lastUpdateTime = Date.now();

    const data = this.viewer.currentData;

    // Backend mode - debounce slice updates to avoid too many API calls
    if (this.viewer.renderingMode === 'geometry') {
      // Clear any pending backend slice computation
      if (this.backendSliceDebounceTimer) {
        clearTimeout(this.backendSliceDebounceTimer);
        this.backendSliceDebounceTimer = null;
      }

      // Skip if already computing
      if (this.isComputingBackendSlice) {
        console.log('⏸️ Skipping slice update (backend slice being computed)');
        return;
      }

      // Schedule slice computation after plane stops moving (800ms debounce)
      console.log('🕐 Scheduling backend slice computation (800ms debounce)...');
      this.backendSliceDebounceTimer = setTimeout(async () => {
        this.backendSliceDebounceTimer = null;
        await this.updateSliceBackend();
      }, 800);

      return;
    }

    // Frontend mode - requires data.grid
    if (!data || !data.grid) return;

    // Get display options from UI checkboxes
    const showContours = document.getElementById('sliceShowContours')?.checked ?? true;
    // AUTO-GENERATE DISABLED: Streamlines only generate via Apply button (unless forceStreamlinesGeneration flag is set)
    const showStreamlines = this.forceStreamlinesGeneration
      ? (document.getElementById('sliceShowStreamlines')?.checked ?? false)
      : false;
    const showVectors = document.getElementById('sliceShowVectors')?.checked ?? false;
    const showIsolines = document.getElementById('sliceShowIsolines')?.checked ?? false;

    // Create new slice using filter manager
    const variable = this.viewer.colormap?.currentField || 'pressure';

    const overlaysRequested = showStreamlines || showVectors || showIsolines;

    // FAST UPDATE: Update the contour slice immediately (needed for overlays too)
    let sliceId = null;
    const existingSlice = this.filterManager.activeFilters.get('slice-filter');

    if (showContours || overlaysRequested) {
      // Recreate slice in place; rendering engine replaces same id automatically
      sliceId = this.filterManager.createSliceUnstructured(data, {
        origin: this.planeOrigin,
        normal: this.planeNormal,
        variable: variable,
        id: 'slice-filter'
      });

      if (sliceId === null && existingSlice) {
        this.filterManager.removeFilter('slice-filter');
      }

      if (sliceId && !showContours) {
        const hiddenSlice = this.filterManager.activeFilters.get('slice-filter');
        if (hiddenSlice?.mesh) {
          hiddenSlice.mesh.visible = false;
        }
      }
    } else {
      // If contours disabled, remove slice
      this.filterManager.removeFilter('slice-filter');
    }

    const sliceCreated = (sliceId !== null);

    // Keep plane/arrow/gizmo visible if no intersection found
    if (!sliceCreated && showContours) {
      console.warn('⚠️ Plane does not intersect mesh - try rotating or moving it');

      // Cancel any pending overlay computation
      if (this.overlayDebounceTimer) {
        clearTimeout(this.overlayDebounceTimer);
        this.overlayDebounceTimer = null;
      }

      // Force widgets to stay visible so user can adjust
      if (this.transformControls) {
        this.transformControls.visible = true;
        this.transformControls.enabled = true;
      }
      if (this.planeGroup) {
        this.planeGroup.visible = true;
      }
      if (this.normalArrow) {
        this.normalArrow.visible = true;
      }

      // Remove any existing overlays
      this.filterManager.removeFilter('slice-streamlines');
      this.filterManager.removeFilter('slice-vectors');
      this.filterManager.removeFilter('slice-isolines');

      return; // Don't compute overlays if no slice exists
    }

    if (!sliceCreated && overlaysRequested) {
      console.warn('⚠️ Plane does not intersect mesh - skipping slice overlays');

      if (this.overlayDebounceTimer) {
        clearTimeout(this.overlayDebounceTimer);
        this.overlayDebounceTimer = null;
      }

      this.filterManager.removeFilter('slice-streamlines');
      this.filterManager.removeFilter('slice-vectors');
      this.filterManager.removeFilter('slice-isolines');

      if (this.transformControls) {
        this.transformControls.visible = true;
        this.transformControls.enabled = true;
      }
      if (this.planeGroup) {
        this.planeGroup.visible = true;
      }
      if (this.normalArrow) {
        this.normalArrow.visible = true;
      }

      return;
    }

    // Coherent visibility logic:
    // - If NO overlays: Show plane, arrow, and gizmo for manipulation
    // - If overlays enabled: Hide everything immediately (will be hidden in debounce too)
    const showingOverlays = overlaysRequested;

    if (showingOverlays) {
      // Hide ALL visual aids when overlays are enabled
      if (this.transformControls) {
        this.transformControls.visible = false;
        this.transformControls.enabled = false;
      }
      if (this.planeGroup) {
        this.planeGroup.visible = false;
      }
      if (this.normalArrow) {
        this.normalArrow.visible = false;
      }
    } else {
      // Show all manipulation widgets when only showing contours
      if (this.transformControls) {
        this.transformControls.visible = true;
        this.transformControls.enabled = true;
      }
      if (this.planeGroup) {
        this.planeGroup.visible = true;
        if (this.planeGroup.children[0]) {
          this.planeGroup.children[0].material.opacity = this.settings.planeOpacity;
        }
      }
      if (this.normalArrow) {
        this.normalArrow.visible = true;
      }
    }

    // DEBOUNCED UPDATE: Compute expensive overlays only when plane stops moving
    if (showingOverlays) {
      // Clear existing overlay timer
      if (this.overlayDebounceTimer) {
        clearTimeout(this.overlayDebounceTimer);
        // Remove old overlays immediately when starting new drag
        this.filterManager.removeFilter('slice-streamlines');
        this.filterManager.removeFilter('slice-vectors');
        this.filterManager.removeFilter('slice-isolines');
      }

      // Schedule overlay computation after plane has been stationary
      this.overlayDebounceTimer = setTimeout(async () => {
        this.isComputingOverlays = true; // Lock to prevent race conditions
        console.log('⏱️ Computing slice overlays (plane stationary)...');

        try {
          // Get fresh data reference
          const currentData = this.viewer.currentData;
          if (!currentData || !currentData.grid) {
            console.error('❌ No data available for overlay computation');
            return;
          }

          // Verify slice still exists (should exist from fast update)
          console.log('🔍 Checking for slice-filter in activeFilters...');
          console.log('Available filters:', Array.from(this.filterManager.activeFilters.keys()));

          let sliceFilter = this.filterManager.activeFilters.get('slice-filter');
          if (!sliceFilter || !sliceFilter.mesh) {
            console.error('❌ Slice not found - cannot compute overlays without slice');
            console.error('Slice filter:', sliceFilter);
            console.warn('💡 Plane may have moved to a position with no intersection or slice was removed');
            console.log('Current plane state - origin:', this.planeOrigin.toArray(), 'normal:', this.planeNormal.toArray());

            // Don't try to recreate - the plane position has likely changed
            // Just skip overlay computation
            return;
          }

          console.log('✅ Slice exists with', sliceFilter.mesh.geometry?.getAttribute('position')?.count, 'vertices');
          console.log('✅ Proceeding with overlay computation');

          // Re-check which overlays to show (checkboxes might have changed)
          const currentShowStreamlines = document.getElementById('sliceShowStreamlines')?.checked ?? false;
          const currentShowVectors = document.getElementById('sliceShowVectors')?.checked ?? false;
          const currentShowIsolines = document.getElementById('sliceShowIsolines')?.checked ?? false;

          // Hide ALL plane visual elements during overlay visualization
          if (currentShowStreamlines || currentShowVectors || currentShowIsolines) {
            if (this.transformControls) {
              this.transformControls.visible = false;
              this.transformControls.enabled = false;
            }
            if (this.planeGroup) {
              this.planeGroup.visible = false;
            }
            if (this.normalArrow) {
              this.normalArrow.visible = false;
            }
          }

          // Compute expensive overlays
          if (currentShowStreamlines) {
            await this.createSliceStreamlines(currentData);
          }
          if (currentShowVectors) {
            this.createSliceVectors(currentData);
          }
          if (currentShowIsolines) {
            this.createSliceIsolines(currentData, variable);
          }

          // Broadcast final state to collaboration after all overlays are created
          if (window.viewer?.viewLockManager) {
            console.log('[Slice Widget] Broadcasting final overlay state to collaboration');
            window.viewer.viewLockManager.onVisualizationChange(true);
          }

          console.log('✅ Slice overlays computed');
        } finally {
          this.isComputingOverlays = false; // Unlock
        }
      }, this.settings.overlayDebounce);
    } else {
      // No overlays needed, remove them immediately and cancel pending computation
      if (this.overlayDebounceTimer) {
        clearTimeout(this.overlayDebounceTimer);
        this.overlayDebounceTimer = null;
      }
      this.filterManager.removeFilter('slice-streamlines');
      this.filterManager.removeFilter('slice-vectors');
      this.filterManager.removeFilter('slice-isolines');
    }

    console.log(`Slice updated: origin=${this.planeOrigin.toArray()}, normal=${this.planeNormal.toArray()}`);
  }

  async updateSliceBackend(options = {}) {
    console.log('[Slice Widget Backend] Updating slice with backend API');

    // Extract cache_key if provided (for collaborative session cache lookup)
    const providedCacheKey = options.cache_key || null;

    const data = this.viewer.currentData;
    if (!data || !this.viewer.geometryParams) {
      console.warn('[Slice Widget Backend] No data or geometry params available');
      return;
    }

    // Prevent concurrent computations - if already computing, queue this request
    if (this.isComputingBackendSlice) {
      console.log('[Slice Widget Backend] Already computing, queuing this request...');
      // Store the force flags for the queued request
      this.pendingSliceRequest = {
        forceContours: this.forceContoursGeneration,
        forceStreamlines: this.forceStreamlinesGeneration,
        forceVectors: this.forceVectorsGeneration,
        forceIsolines: this.forceIsolinesGeneration
      };
      return;
    }

    // Check which features are requested
    // For contours, only generate when forceContoursGeneration flag is set (Apply button clicked)
    const contoursCheckbox = document.getElementById('sliceShowContours');
    const showContours = this.forceContoursGeneration
      ? (contoursCheckbox?.checked ?? false)
      : false;
    // AUTO-GENERATE DISABLED: Streamlines only generate via Apply button (unless forceStreamlinesGeneration flag is set)
    const showStreamlines = this.forceStreamlinesGeneration
      ? (document.getElementById('sliceShowStreamlines')?.checked ?? false)
      : false;
    // Vectors only generate via Apply button (unless forceVectorsGeneration flag is set)
    // Only the slice widget should create vectors, not the streamline widget
    const showVectors = (this.config.inputPrefix === 'slice' && this.forceVectorsGeneration)
      ? (document.getElementById('sliceShowVectors')?.checked ?? false)
      : false;
    const showIsolines = (this.config.inputPrefix === 'slice' && this.forceIsolinesGeneration)
      ? (document.getElementById('sliceShowIsolines')?.checked ?? false)
      : false;

    console.log('[Slice Widget Backend] Display options:', { showContours, showStreamlines, showVectors, showIsolines });
    console.log('[Slice Widget Backend] Force flags:', { forceContours: this.forceContoursGeneration, forceStreamlines: this.forceStreamlinesGeneration, forceVectors: this.forceVectorsGeneration, forceIsolines: this.forceIsolinesGeneration });

    this.isComputingBackendSlice = true;

    try {
      const prefix = this.config.inputPrefix;
      const slicePlaneObjectName = `${prefix}-plane`;

      // Declare result at function scope so it's available for overlays
      let result = null;

      // We need slice geometry for contours, streamlines, vectors, and isolines
      const needSliceGeometry = showContours || showStreamlines || showVectors || showIsolines;

      // Only remove and regenerate if we're actually going to create new geometry
      // This preserves existing contours when the plane is just being moved without Apply
      if (!needSliceGeometry) {
        console.log('[Slice Widget Backend] No slice geometry needed (no force flags set), preserving existing');
        this.isComputingBackendSlice = false;
        return;
      }

      // Remove old slice before creating new one
      this.renderer.removeObject(slicePlaneObjectName);

      // Extract slice geometry from backend
      if (needSliceGeometry) {
        window.loadingProgress.show('Computing slice');
        window.loadingProgress.updateProgress(30, 'Extracting slice plane on backend...');

        // Get authentication token
        const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');

        // Get the selected field for coloring (from contour settings or default to viewer's colorField)
        const contourFieldSelect = document.getElementById('sliceContourField');
        const selectValue = contourFieldSelect?.value;
        const defaultField = this.viewer.geometryParams.colorField;
        const colorField = selectValue || defaultField;

        // Get the selected colormap for contours
        const contourColormapSelect = document.getElementById('sliceContourColormap');
        this.sliceContourColormap = contourColormapSelect?.value || 'jet';

        // Get color range inputs
        const minRangeInput = document.getElementById('sliceContourMinRange');
        const maxRangeInput = document.getElementById('sliceContourMaxRange');
        const minRange = minRangeInput?.value ? parseFloat(minRangeInput.value) : null;
        const maxRange = maxRangeInput?.value ? parseFloat(maxRangeInput.value) : null;

        console.log('[Slice Widget Backend] Color field selection:', {
          selectValue: selectValue,
          defaultField: defaultField,
          usingField: colorField,
          colormap: this.sliceContourColormap,
          colorRange: { min: minRange, max: maxRange },
          selectOptions: contourFieldSelect ? Array.from(contourFieldSelect.options).map(o => o.value) : 'no select element'
        });

        // Get vector field and density settings if vectors are requested
        const vectorField = document.getElementById('sliceVectorField')?.value || 'U';
        const vectorDensity = parseInt(document.getElementById('sliceVectorDensity')?.value) || 200;

        const requestPayload = {
          case_path: this.viewer.geometryParams.casePath,
          time: this.viewer.geometryParams.time,
          color_field: colorField,
          geometry_type: 'slice',
          slice_origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
          slice_normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
          representation: 'surface',
          selected_parts: this.viewer.geometryParams.selectedParts,
          // Vector settings for velocity vectors visualization
          vector_field: vectorField,
          vector_density: vectorDensity,
          // Color range override
          color_range_min: minRange,
          color_range_max: maxRange,
          // Use binary format for faster transfer
          binary: true
        };

        // Add cache_key for collaborative session cache lookup (followers)
        if (providedCacheKey) {
          requestPayload.cache_key = providedCacheKey;
          console.log('[Slice Widget Backend] Using provided cache_key for cache lookup:', providedCacheKey);
        }

        console.log('[Slice Widget Backend] Request payload:', requestPayload);

        // Determine data source (OpenFOAM, EnSight, or Fluent)
        const data = this.viewer.currentData;
        const geometryParams = this.viewer.geometryParams;
        const dataSource = geometryParams?.dataSource;
        let endpoint;

        if (dataSource === 'fluent') {
          endpoint = '/api/fluent/geometry';
        } else if (dataSource === 'vtk') {
          endpoint = '/api/vtk/geometry';
        } else if (dataSource === 'openfoam' || (data?.source === 'OpenFOAM') || (data?.metadata?.format === 'OpenFOAM')) {
          endpoint = '/api/openfoam/geometry';
        } else {
          endpoint = '/api/ensight/geometry';
        }
        console.log('[Slice Widget Backend] Using endpoint:', endpoint, 'dataSource:', dataSource);

        // Call backend to extract slice geometry
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify(requestPayload)
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(error.detail || `Backend slice extraction failed: ${response.statusText}`);
        }

        // Check if response is binary or JSON
        const contentType = response.headers.get('Content-Type') || '';
        const geometryFormat = response.headers.get('X-Geometry-Format');

        // Get cache_key from header (for binary responses) or from JSON body
        let responseCacheKey = response.headers.get('X-Cache-Key') || null;

        if (geometryFormat === 'GEO1' || contentType.includes('octet-stream')) {
          // Parse binary response
          const startTime = performance.now();
          const arrayBuffer = await response.arrayBuffer();
          result = parseBinaryGeometry(arrayBuffer);
          const parseTime = performance.now() - startTime;
          console.log(`[Slice Widget] Binary geometry parsed in ${parseTime.toFixed(1)}ms`);
          // For binary, cache_key comes from header
          if (responseCacheKey) {
            result.cache_key = responseCacheKey;
            console.log('[Slice Widget Backend] Got cache_key from header:', responseCacheKey);
          }
        } else {
          // Parse JSON response (fallback)
          result = await response.json();
          // For JSON, cache_key is in the response body
          if (result.cache_key) {
            responseCacheKey = result.cache_key;
          }
        }
        console.log('[Slice Widget Backend] Received slice data:', result);
        if (result.from_cache) {
          console.log('[Slice Widget Backend] Result was served from cache!');
        }

        window.loadingProgress.updateProgress(70, 'Rendering slice...');

        // Handle geometry data - backend returns nested arrays: [[x,y,z], ...] for vertices, [[i1,i2,i3], ...] for triangles
        // We need to flatten them for THREE.js BufferGeometry
        let vertices = result.vertices ? result.vertices.flat() : null;
        let triangles = result.triangles ? result.triangles.flat() : null;
        let scalars = result.scalars;  // Already flat: [s1, s2, ...]
        let colors = result.colors;
        let scalarRange = result.scalar_range;

        if (result.split_by_parts && result.parts && result.parts.length > 0) {
          console.log('[Slice Widget Backend] Merging', result.parts.length, 'parts');

          // Collect all data from parts
          // Backend returns vertices as [[x,y,z], [x,y,z], ...] (nested arrays)
          // Backend returns triangles as [[i1,i2,i3], ...] (nested arrays)
          // Backend returns scalars as [s1, s2, ...] (flat array, one per vertex)
          const allVertices = [];
          const allTriangles = [];
          const allScalars = [];
          const allColors = [];
          let vertexOffset = 0;

          for (const part of result.parts) {
            if (part.vertices && part.vertices.length > 0) {
              // Flatten and add vertices: [[x,y,z], ...] -> [x,y,z,x,y,z,...]
              for (const v of part.vertices) {
                allVertices.push(v[0], v[1], v[2]);
              }

              // Flatten and add triangles with offset: [[i1,i2,i3], ...] -> [i1,i2,i3,...]
              if (part.triangles) {
                for (const tri of part.triangles) {
                  allTriangles.push(tri[0] + vertexOffset, tri[1] + vertexOffset, tri[2] + vertexOffset);
                }
              }

              // Add scalars (already flat, one per vertex)
              if (part.scalars) {
                for (const s of part.scalars) {
                  allScalars.push(s);
                }
              }

              // Add colors if available
              if (part.colors) {
                for (const c of part.colors) {
                  allColors.push(c);
                }
              }

              // Update offset for next part (part.vertices.length = number of vertices)
              vertexOffset += part.vertices.length;
            }
          }

          vertices = allVertices.length > 0 ? allVertices : null;
          triangles = allTriangles.length > 0 ? allTriangles : null;
          scalars = allScalars.length > 0 ? allScalars : null;
          colors = allColors.length > 0 ? allColors : null;
          scalarRange = result.global_scalar_range || (result.parts[0]?.scalar_range);

          console.log('[Slice Widget Backend] Merged parts:', {
            vertices: vertices?.length,
            vertexCount: vertices ? vertices.length / 3 : 0,
            triangles: triangles?.length,
            triangleCount: triangles ? triangles.length / 3 : 0,
            scalars: scalars?.length,
            colors: colors?.length,
            scalarRange: scalarRange
          });
        }

        // Check if we have vertices and either colors or scalars
        const hasGeometry = result.num_vertices > 0 && vertices;
        const hasColors = colors && colors.length > 0;
        const hasScalars = scalars && scalars.length > 0;

        if (hasGeometry && (hasColors || hasScalars)) {
          console.log('[Slice Widget Backend] Creating slice mesh:', {
            vertices: vertices.length / 3,
            triangles: triangles ? triangles.length / 3 : 0,
            hasColors: hasColors,
            hasScalars: hasScalars
          });

          // Create geometry from backend data
          const geometry = new THREE.BufferGeometry();

          // Vertices
          const positions = new Float32Array(vertices);
          geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

          // Colors - either use pre-computed colors or convert scalars to colors
          let colorsArray;
          const preset = this.sliceContourColormap || this.viewer?.colormap?.preset || 'jet';
          const usingSolidColor = Colormap.isSolidColor(preset);

          // If using solid color mode, we MUST use local color computation (ignore backend colors)
          // This ensures the slice uses its own solid color picker, not the mesh's color
          if (usingSolidColor && hasScalars) {
            // Force local solid color processing
            const solidColorHex = Colormap.getSolidColor('sliceContourColormap');
            console.log('[Slice Widget Backend] Using local solid color:', solidColorHex);

            // Apply solid color to all vertices
            colorsArray = new Float32Array(scalars.length * 3);
            const rgb = Colormap.hexToRGB(solidColorHex);
            for (let i = 0; i < scalars.length; i++) {
              colorsArray[i * 3] = rgb[0] / 255;
              colorsArray[i * 3 + 1] = rgb[1] / 255;
              colorsArray[i * 3 + 2] = rgb[2] / 255;
            }
          } else if (hasColors && !usingSolidColor) {
            // Backend provided pre-computed colors (only use when NOT in solid color mode)
            colorsArray = new Float32Array(colors);
          } else if (hasScalars) {
            // Convert scalars to colors using colormap
            const effectiveRange = scalarRange || { min: Math.min(...scalars), max: Math.max(...scalars) };

            // Use custom range if provided, otherwise use auto range
            const min = (minRange !== null && Number.isFinite(minRange)) ? minRange : effectiveRange.min;
            const max = (maxRange !== null && Number.isFinite(maxRange)) ? maxRange : effectiveRange.max;

            // Use Colormap utility to convert scalars to RGB colors
            // Note: preset is already defined above, and we know we're not in solid color mode here
            const rgbColors = Colormap.mapDataToColors(scalars, min, max, preset, null);

            // Convert to Float32Array with normalized values (0-1 range for THREE.js)
            colorsArray = new Float32Array(scalars.length * 3);
            for (let i = 0; i < rgbColors.length; i++) {
              colorsArray[i * 3] = rgbColors[i][0] / 255;
              colorsArray[i * 3 + 1] = rgbColors[i][1] / 255;
              colorsArray[i * 3 + 2] = rgbColors[i][2] / 255;
            }
          }

          geometry.setAttribute('color', new THREE.BufferAttribute(colorsArray, 3));

          // Triangles (if available)
          if (triangles && triangles.length > 0) {
            const indices = new Uint32Array(triangles);
            geometry.setIndex(new THREE.BufferAttribute(indices, 1));
          }

          // Create material - make invisible if only streamlines requested (not contours)
          // Use polygonOffset to prevent z-fighting with the main geometry
          const material = new THREE.MeshBasicMaterial({
            vertexColors: THREE.VertexColors,
            side: THREE.DoubleSide,
            visible: showContours,  // Only visible if contours are enabled
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
            depthTest: true,
            depthWrite: true
          });

          // Create mesh
          const mesh = new THREE.Mesh(geometry, material);
          mesh.visible = showContours;  // Hide mesh if only using it for seed sampling
          mesh.renderOrder = 1;  // Render after main geometry

          // Add to scene (needed for seed sampling even if invisible)
          // Use prefix-based name to avoid conflicts between widgets
          this.renderer.addObject(slicePlaneObjectName, mesh);

          // Register as active filter for collaboration sync
          const contourFieldSelect = document.getElementById('sliceContourField');
          const contourColormapSelect = document.getElementById('sliceContourColormap');

          // Use custom range for legend if provided, otherwise use auto range
          const legendRange = {
            min: (minRange !== null && Number.isFinite(minRange)) ? minRange : scalarRange?.min,
            max: (maxRange !== null && Number.isFinite(maxRange)) ? maxRange : scalarRange?.max
          };

          const storedOptions = {
            origin: this.planeOrigin.toArray(),
            normal: this.planeNormal.toArray(),
            colorField: contourFieldSelect?.value || 'p',
            colormap: contourColormapSelect?.value || 'jet',
            scalarRange: legendRange,
            // Store cache_key for collaborative session optimization
            // Followers can use this to fetch cached results from backend
            cache_key: result.cache_key || responseCacheKey || null
          };
          if (storedOptions.cache_key) {
            console.log('[Slice Widget Backend] Stored cache_key for collaboration:', storedOptions.cache_key);
          }

          if (this.filterManager && this.filterManager.activeFilters) {
            this.filterManager.activeFilters.set('slice-contours', {
              type: 'slice_contours',
              mesh: mesh,
              options: storedOptions
            });
            console.log('[Slice Widget Backend] Registered slice-contours filter for collaboration:', storedOptions);
            console.log('[Slice Widget Backend] Active filters now:', Array.from(this.filterManager.activeFilters.keys()));
            // Note: ViewLock broadcast will happen at the end of updateSliceBackend to avoid intermediate state broadcasts
          } else {
            console.warn('[Slice Widget Backend] filterManager or activeFilters not available');
          }

          // Update slice legend
          if (this.viewer && showContours) {
            this.viewer.updateSliceLegend('slice-contours', storedOptions);
          }

          console.log('[Slice Widget Backend] Slice mesh created (visible:', showContours, ')');
        } else {
          console.warn('[Slice Widget Backend] No slice geometry returned:', {
            num_vertices: result.num_vertices,
            has_vertices: !!vertices,
            has_colors: hasColors,
            has_scalars: hasScalars
          });
          Utils.notify('Slice plane does not intersect mesh - try adjusting the plane position or orientation', 'warning');
        }

        window.loadingProgress.complete('Slice computed');
      }

      // Compute overlays if requested (streamlines, vectors, isolines)
      if (showStreamlines || showVectors || showIsolines) {
        console.log('[Slice Widget Backend] Computing slice overlays...');

        if (showStreamlines) {
          await this.createSliceStreamlines(data);
        }

        // Velocity vectors from backend data (use 'result' which is the backend response)
        // Only create vectors if we have actual slice geometry (num_vertices > 0)
        if (showVectors && result && result.num_vertices > 0) {
          await this.createSliceVectorsBackend(result);
        } else if (showVectors && result && result.num_vertices === 0) {
          console.log('[Slice Widget Backend] Skipping vectors - no slice geometry in this response');
        }

        // Iso-lines (contour lines) on slice
        if (showIsolines && result && result.num_vertices > 0) {

          // Check if isolines need a different field than contours
          const isolinesFieldSelect = document.getElementById('sliceIsolinesField');
          const contourFieldSelect = document.getElementById('sliceContourField');
          const currentContourField = contourFieldSelect?.value || this.viewer.geometryParams.colorField;
          const isolinesField = isolinesFieldSelect?.value || currentContourField;

          if (isolinesField && isolinesField !== currentContourField) {
            // Fetch slice data with the isolines field
            console.log('[Slice Widget Backend] Fetching isolines with different field:', isolinesField);

            const authToken = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
            const data = this.viewer.currentData;
            const geometryParams = this.viewer.geometryParams;
            const dataSource = geometryParams?.dataSource;
            let apiEndpoint;
            if (dataSource === 'fluent') {
              apiEndpoint = '/api/fluent/geometry';
            } else if (dataSource === 'vtk') {
              apiEndpoint = '/api/vtk/geometry';
            } else if (dataSource === 'openfoam' || (data?.source === 'OpenFOAM') || (data?.metadata?.format === 'OpenFOAM')) {
              apiEndpoint = '/api/openfoam/geometry';
            } else {
              apiEndpoint = '/api/ensight/geometry';
            }

            const isolinesPayload = {
              case_path: this.viewer.geometryParams.casePath,
              time: this.viewer.geometryParams.time,
              color_field: isolinesField,
              geometry_type: 'slice',
              slice_origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
              slice_normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
              representation: 'surface',
              selected_parts: this.viewer.geometryParams.selectedParts,
              binary: true  // Use binary format for faster transfer
            };

            const isolinesResponse = await fetch(apiEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': authToken ? `Bearer ${authToken}` : ''
              },
              body: JSON.stringify(isolinesPayload)
            });

            if (isolinesResponse.ok) {
              // Check if response is binary or JSON
              const isoContentType = isolinesResponse.headers.get('Content-Type') || '';
              const isoGeometryFormat = isolinesResponse.headers.get('X-Geometry-Format');
              let isolinesResult;

              if (isoGeometryFormat === 'GEO1' || isoContentType.includes('octet-stream')) {
                const arrayBuffer = await isolinesResponse.arrayBuffer();
                isolinesResult = parseBinaryGeometry(arrayBuffer);
              } else {
                isolinesResult = await isolinesResponse.json();
              }
              console.log('[Slice Widget Backend] Isolines field data:', {
                field: isolinesField,
                scalarRange: isolinesResult.scalar_range
              });
              // Use isolines field scalars but keep original geometry
              await this.createSliceIsolinesBackend({
                ...result,
                scalars: isolinesResult.scalars,
                scalar_range: isolinesResult.scalar_range
              });
            } else {
              console.warn('[Slice Widget Backend] Failed to fetch isolines field, using contour field');
              await this.createSliceIsolinesBackend(result);
            }
          } else {
            // Use same field as contours
            await this.createSliceIsolinesBackend(result);
          }

        } else if (showIsolines && result && result.num_vertices === 0) {
          console.log('[Slice Widget Backend] Skipping isolines - no slice geometry in this response');
        }
      }

      // Broadcast final state to collaboration after all slice elements are created
      // This ensures contours, vectors, and isolines are all included in a single broadcast
      if (window.viewer?.viewLockManager) {
        console.log('[Slice Widget Backend] Broadcasting final slice state to collaboration');
        console.log('[Slice Widget Backend] Active filters:', Array.from(this.filterManager?.activeFilters?.keys() || []));
        window.viewer.viewLockManager.onVisualizationChange(true);  // Force immediate
      }

    } catch (error) {
      console.error('[Slice Widget Backend] Error updating slice:', error);
      window.loadingProgress.error(error.message);
      Utils.notify('Failed to compute slice: ' + error.message, 'error');
    } finally {
      // Clear the computing flag
      this.isComputingBackendSlice = false;

      // Reset the force flags after computation completes
      // This allows the Apply buttons to work for the next click
      this.forceStreamlinesGeneration = false;
      this.forceContoursGeneration = false;
      this.forceVectorsGeneration = false;
      this.forceIsolinesGeneration = false;

      // Process any queued request
      if (this.pendingSliceRequest) {
        console.log('[Slice Widget Backend] Processing queued request:', this.pendingSliceRequest);
        const pending = this.pendingSliceRequest;
        this.pendingSliceRequest = null;
        // Restore the force flags from the queued request
        this.forceContoursGeneration = pending.forceContours;
        this.forceStreamlinesGeneration = pending.forceStreamlines;
        this.forceVectorsGeneration = pending.forceVectors;
        this.forceIsolinesGeneration = pending.forceIsolines;
        // Process the queued request
        this.updateSliceBackend();
      }
    }
  }

  removeSliceStreamlines() {
    // Remove slice streamlines filter
    if (this.filterManager) {
      this.filterManager.removeFilter('slice-streamlines');
      console.log('[Slice Widget] Removed slice streamlines');
    }

    // Hide the streamline legend if it's showing slice streamlines
    const legend = document.getElementById('streamlineLegend');
    if (legend && legend.dataset.filterId === 'slice-streamlines') {
      if (this.viewer && typeof this.viewer.hideStreamlineLegend === 'function') {
        this.viewer.hideStreamlineLegend();
      }
    }

    // Remove seed line preview (use prefix to avoid conflicts)
    this.stopSeedLineBlinking();
    if (this.seedLinePreview) {
      const prefix = this.config.inputPrefix;
      const seedLinePreviewName = `${prefix}-seed-line-preview`;
      this.renderer.removeObject(seedLinePreviewName);
      this.seedLinePreview = null;
      this.seedLineIndicator = null;
    }

    // Remove streamline helper if it exists
    if (this.viewer?.streamlineHelpers?.['slice-streamlines']) {
      const helper = this.viewer.streamlineHelpers['slice-streamlines'];
      helper.visible = false;
      if (helper.parent) {
        helper.parent.remove(helper);
      }
      delete this.viewer.streamlineHelpers['slice-streamlines'];
    }
  }

  removeSlice() {
    // Remove streamlines first (includes legend cleanup)
    this.removeSliceStreamlines();

    // Remove velocity vectors
    this.removeSliceVectors();

    // Backend mode - remove slice object from scene
    if (this.viewer.renderingMode === 'geometry') {
      // Use prefix-based name to avoid conflicts between widgets
      const prefix = this.config.inputPrefix;
      const slicePlaneObjectName = `${prefix}-plane`;
      this.renderer.removeObject(slicePlaneObjectName);
      console.log(`[${prefix} Widget Backend] Removed ${prefix} plane`);

      // Also remove from active filters for collaboration sync
      if (this.filterManager && this.filterManager.activeFilters) {
        this.filterManager.activeFilters.delete('slice-contours');
        this.filterManager.activeFilters.delete('slice-vectors');
        this.filterManager.activeFilters.delete('slice-isolines');
        console.log(`[${prefix} Widget Backend] Removed slice filters from activeFilters`);
      }

      // Hide the slice legend and isolines legend
      if (this.viewer) {
        this.viewer.hideSliceLegend();
        if (typeof this.viewer.hideIsolinesLegend === 'function') {
          this.viewer.hideIsolinesLegend();
        }
      }
    } else {
      // Frontend mode - remove filters
      if (this.filterManager) {
        this.filterManager.removeFilter('slice-filter');
        this.filterManager.removeFilter('slice-vectors');
        this.filterManager.removeFilter('slice-isolines');
      }
    }
  }

  removePlaneHelper() {
    if (this.planeGroup) {
      this.scene.remove(this.planeGroup);

      // Dispose geometries and materials
      this.planeGroup.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });

      this.planeGroup = null;
      this.planeHelper = null;
    }
  }

  removeNormalArrow() {
    if (this.normalArrow) {
      this.scene.remove(this.normalArrow);

      // Dispose
      this.normalArrow.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });

      this.normalArrow = null;
    }
  }

  /**
   * Calculate plane width and height based on the mesh bounding box and plane normal.
   * If manual width/height are set (non-zero), use those instead of auto-calculating.
   */
  calculatePlaneSize(bbox) {
    const prefix = this.config.inputPrefix;

    // Check if manual width/height are specified
    const manualWidth = parseFloat(document.getElementById(`${prefix}PlaneWidth`)?.value || 0);
    const manualHeight = parseFloat(document.getElementById(`${prefix}PlaneHeight`)?.value || 0);

    if (manualWidth > 0 && manualHeight > 0) {
      // Use manual values
      this.planeWidth = manualWidth;
      this.planeHeight = manualHeight;
      console.log(`[${prefix} Widget] Using manual plane size: ${this.planeWidth.toFixed(2)} x ${this.planeHeight.toFixed(2)}`);
      return;
    }

    // Auto-calculate based on which dimensions the plane spans
    const size = bbox.size;
    const normal = this.planeNormal;
    const margin = 2.0; // 2x the mesh size for better visibility

    // Determine which dimensions the plane spans based on its normal
    // If normal is X, plane spans Y and Z
    // If normal is Y, plane spans X and Z
    // If normal is Z, plane spans X and Y
    const absNormal = new THREE.Vector3(Math.abs(normal.x), Math.abs(normal.y), Math.abs(normal.z));

    if (absNormal.x > absNormal.y && absNormal.x > absNormal.z) {
      // X-normal plane spans Y and Z
      this.planeWidth = size.y * margin;
      this.planeHeight = size.z * margin;
    } else if (absNormal.y > absNormal.x && absNormal.y > absNormal.z) {
      // Y-normal plane spans X and Z
      this.planeWidth = size.x * margin;
      this.planeHeight = size.z * margin;
    } else {
      // Z-normal plane spans X and Y
      this.planeWidth = size.x * margin;
      this.planeHeight = size.y * margin;
    }

    // Update the input fields to show auto-calculated values (set to 0 to indicate auto)
    const widthInput = document.getElementById(`${prefix}PlaneWidth`);
    const heightInput = document.getElementById(`${prefix}PlaneHeight`);
    if (widthInput) widthInput.value = '0';
    if (heightInput) heightInput.value = '0';

    console.log(`[${prefix} Widget] Auto-calculated plane size: ${this.planeWidth.toFixed(2)} x ${this.planeHeight.toFixed(2)}`);
  }

  /**
   * Update plane size from manual width/height inputs
   */
  updatePlaneSizeFromInputs() {
    const prefix = this.config.inputPrefix;
    const widthInput = document.getElementById(`${prefix}PlaneWidth`);
    const heightInput = document.getElementById(`${prefix}PlaneHeight`);
    const width = parseFloat(widthInput?.value || 0);
    const height = parseFloat(heightInput?.value || 0);

    console.log(`[${prefix} Widget] updatePlaneSizeFromInputs called - width: ${width}, height: ${height}, enabled: ${this.enabled}`);

    if (!this.enabled) {
      console.log(`[${prefix} Widget] Widget not enabled, skipping size update`);
      return;
    }

    if (width > 0 && height > 0) {
      // Use manual values
      console.log(`[${prefix} Widget] Setting manual size: ${width} x ${height}`);
      this.planeWidth = width;
      this.planeHeight = height;

      // Recreate the plane with new size
      console.log(`[${prefix} Widget] Removing old plane...`);
      this.removePlaneHelper();
      console.log(`[${prefix} Widget] Creating new plane...`);
      this.createPlaneHelper();
      this.scene.add(this.planeGroup);  // Add plane back to scene
      this.updatePlaneTransform();

      // Reattach transform controls to the new plane
      if (this.transformControls) {
        console.log(`[${prefix} Widget] Reattaching transform controls...`);
        this.transformControls.attach(this.planeGroup);
      }

      // Force render
      if (this.renderer && this.renderer.render) {
        console.log(`[${prefix} Widget] Forcing render...`);
        this.renderer.render();
      }

      console.log(`[${prefix} Widget] ✓ Updated plane size to: ${this.planeWidth.toFixed(2)} x ${this.planeHeight.toFixed(2)}`);

      // Notify external listeners
      this.notifyPlaneChange();
    } else if (width === 0 && height === 0) {
      // Reset to auto-sizing - recalculate from bounding box
      const data = this.viewer.currentData;
      let bbox;
      if (this.viewer.renderingMode === 'geometry') {
        bbox = this.getBoundingBoxFromScene();
      } else if (data && data.grid) {
        bbox = this.calculateBoundingBox(data.grid);
      }

      if (bbox) {
        this.calculatePlaneSize(bbox);

        // Recreate the plane with new size
        this.removePlaneHelper();
        this.createPlaneHelper();
        this.scene.add(this.planeGroup);  // Add plane back to scene
        this.updatePlaneTransform();

        // Reattach transform controls to the new plane
        if (this.transformControls) {
          this.transformControls.attach(this.planeGroup);
        }

        // Force render
        if (this.renderer && this.renderer.render) {
          this.renderer.render();
        }

        // Notify external listeners
        this.notifyPlaneChange();
      }
    }
  }

  removeTransformControls() {
    if (this.transformControls) {
      this.transformControls.enabled = false;
      this.transformControls.visible = false;
      this.transformControls.detach();

      // Force hide the gizmo
      if (this.transformControls.children) {
        this.transformControls.children.forEach(child => {
          child.visible = false;
        });
      }

      this.scene.remove(this.transformControls);
      this.transformControls.dispose();
      this.transformControls = null;

      // Force a render to ensure gizmo disappears
      if (this.renderer && this.renderer.render) {
        this.renderer.render();
      }
    }

    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  // Public API methods

  setPlaneSize(width, height) {
    this.planeWidth = width;
    this.planeHeight = height || width; // Use width for height if not specified
    if (this.enabled) {
      this.removePlaneHelper();
      this.createPlaneHelper();
      this.scene.add(this.planeGroup);  // Add plane back to scene
      this.updatePlaneTransform();

      // Reattach transform controls to the new plane
      if (this.transformControls) {
        this.transformControls.attach(this.planeGroup);
      }
    }
  }

  setPlaneOrientation(nx, ny, nz) {
    // Set the plane normal to the specified orientation
    this.planeNormal.set(nx, ny, nz).normalize();

    if (this.enabled && this.planeGroup) {
      // Update the plane's rotation to match the new normal
      const quaternion = new THREE.Quaternion();
      const up = new THREE.Vector3(0, 0, 1);  // Default plane normal in local space
      quaternion.setFromUnitVectors(up, this.planeNormal);
      this.planeGroup.setRotationFromQuaternion(quaternion);

      // Update the arrow if it exists
      this.updateNormalArrow();

      // Update the hidden input fields
      this.updateInputFields();

      // Force render
      if (this.renderer && this.renderer.render) {
        this.renderer.render();
      }

      console.log(`[${this.config.inputPrefix} Widget] Set orientation to normal: (${nx}, ${ny}, ${nz})`);

      // Notify external listeners
      this.notifyPlaneChange();
    }
  }

  setPlaneColor(color) {
    this.settings.planeColor = color;
    if (this.planeHelper) {
      this.planeHelper.material.color.set(color);
    }
  }

  setPlaneOpacity(opacity) {
    this.settings.planeOpacity = opacity;
    if (this.planeHelper) {
      this.planeHelper.material.opacity = opacity;
    }
  }

  setTransformMode(mode) {
    if (this.transformControls && ['translate', 'rotate'].includes(mode)) {
      this.transformControls.setMode(mode);
    }
  }

  // ========================================================================
  // SLICE OVERLAY FEATURES
  // ========================================================================

  async createSliceStreamlines(data) {
    console.log('🌊 createSliceStreamlines called');

    // Get user-specified seed count from UI
    const seedCountInput = document.getElementById('sliceStreamlineSeedCount');
    const seedCount = seedCountInput ? parseInt(seedCountInput.value) : 100;

    let seedPoints = [];

    if (this.viewer.renderingMode === 'geometry') {
      // Backend mode - generate seeds along a line for perfect spreading
      // More robust than requiring slice mesh extraction (which may fail)

      // Read rotation and offset from UI
      const rotationInput = document.getElementById('sliceStreamlineRotation');
      const offsetInput = document.getElementById('sliceStreamlineOffset');
      const rotation = rotationInput ? parseFloat(rotationInput.value) : 45;
      const offset = offsetInput ? parseFloat(offsetInput.value) : 0;

      seedPoints = this.generateSeedPointsOnPlaneWithinMesh(seedCount, rotation, offset);
      console.log(`✅ Generated ${seedPoints.length} seed points along line (backend mode, ${rotation}°, ${offset}%)`);
    } else {
      // Frontend mode - sample from slice mesh
      const sliceFilter = this.filterManager.activeFilters.get('slice-filter');

      if (!sliceFilter) {
        console.error('❌ No slice-filter found in activeFilters');
        console.log('Available filters:', Array.from(this.filterManager.activeFilters.keys()));
        return;
      }

      const sliceMesh = sliceFilter.mesh;
      if (!sliceMesh || !sliceMesh.geometry) {
        console.error('❌ Slice mesh or geometry is null');
        return;
      }

      console.log('✅ Slice mesh found, geometry has', sliceMesh.geometry.getAttribute('position')?.count, 'vertices');

      // Sample seed points uniformly from the slice mesh surface
      seedPoints = this.samplePointsOnSlice(sliceMesh, data, seedCount);
      console.log(`✅ Sampled ${seedPoints.length} seed points from slice mesh (frontend mode)`);
    }

    if (seedPoints.length === 0) {
      console.warn('❌ No seed points sampled from slice');
      return;
    }

    console.log(`✅ Sampled ${seedPoints.length} seed points on slice plane`);
    console.log('First seed point:', seedPoints[0]);

    const colorFieldSelect = document.getElementById('sliceStreamlineColorField');
    let colorField = colorFieldSelect ? colorFieldSelect.value : '';
    if (colorField) {
      colorField = colorField.trim();
    }
    if (!colorField) {
      colorField = null;
    }

    // Use the existing streamline infrastructure with custom seed points
    try {
      console.log('🚀 Calling filterManager.createStreamlines...');

      // IMPORTANT: Force lines mode for slice streamlines (ignore UI dropdown)
      // Save current render mode
      const streamlineRenderModeDropdown = document.getElementById('streamlineRenderMode');
      const originalMode = streamlineRenderModeDropdown?.value;

      // Temporarily set to lines
      if (streamlineRenderModeDropdown) {
        streamlineRenderModeDropdown.value = 'lines';
      }

      // Get bounds - different for frontend vs backend mode
      let bounds = null;
      let domainSpan = 1;
      let domainDiagonal = 1;

      if (this.viewer.renderingMode === 'geometry') {
        // Backend mode - get bounds from scene mesh
        const bbox = this.getBoundingBoxFromScene();
        if (bbox) {
          bounds = {
            xmin: bbox.min.x,
            xmax: bbox.max.x,
            ymin: bbox.min.y,
            ymax: bbox.max.y,
            zmin: bbox.min.z,
            zmax: bbox.max.z
          };
          const spanX = bbox.size.x;
          const spanY = bbox.size.y;
          const spanZ = bbox.size.z;
          domainSpan = Math.max(spanX, spanY, spanZ, 1);
          domainDiagonal = Math.sqrt(spanX * spanX + spanY * spanY + spanZ * spanZ);

          console.log('[Slice Streamlines Backend] Mesh bounds:', bounds);
          console.log('[Slice Streamlines Backend] Domain span:', domainSpan, 'diagonal:', domainDiagonal);
        }
      } else {
        // Frontend mode - get bounds from data
        bounds = data?.grid?.bounds || null;
        if (bounds) {
          const spanX = bounds.xmax - bounds.xmin;
          const spanY = bounds.ymax - bounds.ymin;
          const spanZ = bounds.zmax - bounds.zmin;
          domainSpan = Math.max(spanX, spanY, spanZ, 1);
          domainDiagonal = Math.sqrt(spanX * spanX + spanY * spanY + spanZ * spanZ);
        }
      }

      // For slice streamlines, use MUCH shorter length - constrained to slice plane
      // Use plane size instead of full domain
      const planeSize = this.planeSize || domainSpan;
      const fullLength = Math.min(planeSize * 1.5, domainDiagonal * 0.5);  // Much shorter!
      const adaptiveStep = Math.max(domainSpan / 1000, 0.0001);

      console.log('[Slice Streamlines] Max length:', fullLength, 'Step size:', adaptiveStep);

      // Get vector field from selector
      const vectorFieldSelect = document.getElementById('sliceStreamlineVectorField');
      const vectorField = vectorFieldSelect ? vectorFieldSelect.value : 'U';

      // Get colormap preset from selector
      const colormapSelect = document.getElementById('sliceStreamlineColormap');
      const colormapPreset = colormapSelect ? colormapSelect.value : 'jet';

      const streamOptions = {
        seedPoints: seedPoints,
        seedType: 'point',  // Keep as 'point' but skip helper with flag
        variable: vectorField,
        maxLength: fullLength,
        stepSize: adaptiveStep,
        direction: 'both',
        renderMode: 'lines',  // Force lines mode (no particles/tubes/ribbons)
        skipSeedHelper: true,  // Skip seed visualization helper (sphere/gizmo)
        previewEnabled: false, // Disable moving preview dots for slice overlays
        showSeedGizmo: false,
        id: 'slice-streamlines',
        colormap: colormapPreset  // Add colormap preset
      };
      if (colorField) {
        streamOptions.colorField = colorField;
      }

      console.log('[Slice Streamlines] Using colormap:', colormapPreset);

      console.log('[Slice Streamlines] Using vector field:', vectorField);

      const result = await this.filterManager.createStreamlines(data, streamOptions);

      // Restore original render mode
      if (streamlineRenderModeDropdown && originalMode) {
        streamlineRenderModeDropdown.value = originalMode;
      }

      // IMPORTANT: Stop particle animator if it's running (particles interfere with slice viz)
      if (window.particleAnimator && window.particleAnimator.isRunning && window.particleAnimator.isRunning()) {
        console.log('⏸️ Stopping particle animator for clean slice streamlines');
        window.particleAnimator.stop();
      }

      console.log('✅ Slice streamlines created, result:', result);

      // Remove seed line preview now that streamlines are loaded (use prefix to avoid conflicts)
      this.stopSeedLineBlinking();
      if (this.seedLinePreview) {
        const prefix = this.config.inputPrefix;
        const seedLinePreviewName = `${prefix}-seed-line-preview`;
        this.renderer.removeObject(seedLinePreviewName);
        this.seedLinePreview = null;
        this.seedLineIndicator = null;
      }

      // Update the streamline legend
      if (this.viewer && typeof this.viewer.updateStreamlineLegend === 'function') {
        const streamlineFilter = this.filterManager.activeFilters.get('slice-streamlines');
        if (streamlineFilter && streamlineFilter.options) {
          console.log('[Slice Streamlines] Updating legend with options:', streamlineFilter.options);
          this.viewer.updateStreamlineLegend('slice-streamlines', streamlineFilter.options);
        }
      }

      // Force remove seed helper visualization (the big dot/sphere)
      setTimeout(() => {
        const streamlineHelper = this.viewer?.streamlineHelpers?.['slice-streamlines'];
        if (streamlineHelper) {
          console.log('🗑️ Removing seed helper for slice streamlines');
          streamlineHelper.visible = false;
          if (streamlineHelper.parent) {
            streamlineHelper.parent.remove(streamlineHelper);
          }
          delete this.viewer.streamlineHelpers['slice-streamlines'];
        }

        // Debug: Check streamlines visibility
        const streamlineFilter = this.filterManager.activeFilters.get('slice-streamlines');
        if (streamlineFilter && streamlineFilter.mesh) {
          console.log('Streamline mesh visible:', streamlineFilter.mesh.visible);
          console.log('Streamline mesh children:', streamlineFilter.mesh.children?.length);
        }
      }, 200);
    } catch (error) {
      console.error('❌ Failed to create slice streamlines:', error);
      console.error('Error stack:', error.stack);
    }
  }

  updateSliceStreamlineColors() {
    /**
     * Update streamline colors locally without reloading from backend.
     * This is much faster than re-computing streamlines!
     */
    console.log('🎨 Updating slice streamline colors locally (no backend reload)');

    const streamlineFilter = this.filterManager.activeFilters.get('slice-streamlines');
    if (!streamlineFilter) {
      console.warn('No slice streamlines to recolor');
      return;
    }

    // Get the new colormap from UI
    const colormapSelect = document.getElementById('sliceStreamlineColormap');
    const newColormap = colormapSelect ? colormapSelect.value : 'jet';

    console.log(`Applying new colormap: ${newColormap}`);

    // Update the filter options
    if (streamlineFilter.options) {
      streamlineFilter.options.colormap = newColormap;
    }

    // Get the mesh group (streamlines are a group of line segments)
    const meshGroup = streamlineFilter.mesh;
    if (!meshGroup) {
      console.warn('No streamline mesh found');
      return;
    }

    // Get scalar range from stored data
    const scalarRange = streamlineFilter.scalarRange || streamlineFilter.options?.scalarRange;
    if (!scalarRange) {
      console.warn('No scalar range found for recoloring');
      return;
    }

    console.log(`Recoloring ${meshGroup.children.length} streamline segments with ${newColormap}`);

    // Iterate through all children (each streamline is a separate LineSegments object)
    let totalVerticesUpdated = 0;
    meshGroup.children.forEach((child) => {
      if (!child.geometry || !child.geometry.attributes.color) {
        return; // Skip non-geometry children
      }

      // Get the color attribute
      const colorAttribute = child.geometry.attributes.color;
      const colors = colorAttribute.array;

      // Get scalar attribute (if exists) or use userData
      let scalars = null;
      if (child.geometry.attributes.scalar) {
        scalars = child.geometry.attributes.scalar.array;
      } else if (child.userData && child.userData.scalars) {
        scalars = child.userData.scalars;
      }

      if (!scalars) {
        console.warn('No scalars found for child, skipping');
        return;
      }

      // Recompute colors for this line segment
      // Get solid color for this dropdown if using solid colormap
      const solidColorHex = Colormap.isSolidColor(newColormap) ? Colormap.getSolidColor('sliceStreamlineColormap') : null;

      const numVertices = colors.length / 3;
      for (let i = 0; i < numVertices; i++) {
        const scalarValue = scalars[i];
        const t = (scalarValue - scalarRange.min) / (scalarRange.max - scalarRange.min);
        const rgb = Colormap.getColor(t, newColormap, solidColorHex);
        colors[i * 3] = rgb[0];
        colors[i * 3 + 1] = rgb[1];
        colors[i * 3 + 2] = rgb[2];
      }

      // Mark for update
      colorAttribute.needsUpdate = true;
      totalVerticesUpdated += numVertices;
    });

    console.log(`✅ Updated colors for ${totalVerticesUpdated} vertices`);

    // Update legend
    if (this.viewer && typeof this.viewer.updateStreamlineLegend === 'function') {
      this.viewer.updateStreamlineLegend('slice-streamlines', streamlineFilter.options);
    }

    console.log('✅ Streamline colors updated locally (no backend call!)');
  }

  createSliceVectors(data) {
    // Create velocity vector arrows on the slice plane
    const sliceMesh = this.filterManager.activeFilters.get('slice-filter')?.mesh;
    if (!sliceMesh || !sliceMesh.geometry) return;

    const velocityField = data.fields['velocity'] || data.fields['U'];
    if (!velocityField) {
      console.warn('No velocity field found for vectors');
      return;
    }

    // Sample points on slice (fewer than streamlines, for clarity)
    const samplePoints = this.samplePointsOnSlice(sliceMesh, data, 20);

    const arrowGroup = new THREE.Group();
    arrowGroup.name = 'slice-vectors';

    samplePoints.forEach(point => {
      const nearestIdx = this.findNearestVertex(data.grid.vertices, point);
      const velocity = velocityField[nearestIdx];

      if (velocity && velocity.length >= 3) {
        const vel3D = new THREE.Vector3(velocity[0], velocity[1], velocity[2]);

        // Project velocity onto plane
        const projectedVel = vel3D.clone().sub(
          this.planeNormal.clone().multiplyScalar(vel3D.dot(this.planeNormal))
        );

        const magnitude = projectedVel.length();
        if (magnitude > 1e-6) {
          const direction = projectedVel.normalize();
          const length = Math.min(magnitude * 0.5, this.planeSize * 0.1);

          const arrow = new THREE.ArrowHelper(
            direction,
            new THREE.Vector3(...point),
            length,
            0x00ff00,  // Green arrows
            length * 0.2,  // Head length
            length * 0.15   // Head width
          );
          arrowGroup.add(arrow);
        }
      }
    });

    this.scene.add(arrowGroup);

    // Store in activeFilters for collaboration sync with all options needed to recreate
    this.filterManager.activeFilters.set('slice-vectors', {
      type: 'slice_vectors',
      mesh: arrowGroup,
      options: {
        origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
        normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
        vectorField: document.getElementById('sliceVectorField')?.value || 'U',
        vectorScale: parseFloat(document.getElementById('sliceVectorScale')?.value) || 1.0,
        vectorDensity: parseInt(document.getElementById('sliceVectorDensity')?.value) || 200,
        colormap: document.getElementById('sliceVectorColormap')?.value || 'jet',
        vectorMode: document.getElementById('sliceVectorMode')?.value || 'uniform'
      }
    });
    // Note: ViewLock broadcast happens at the end of updateSlice to avoid intermediate state broadcasts

    console.log(`Created ${arrowGroup.children.length} velocity vectors on slice`);
  }

  /**
   * Create velocity vectors from backend-extracted data (geometry mode)
   * Uses vectors and vector_positions from _extract_mesh_geometry response
   */
  async createSliceVectorsBackend(data) {
    // Remove any existing vectors first
    this.removeSliceVectors();

    // Check if backend returned vector data
    if (!data.vectors || !data.vector_positions || data.vectors.length === 0) {
      console.warn('[Slice Widget Backend] No velocity vectors in response');
      Utils.notify('No velocity vectors available on slice', 'warning');
      return;
    }

    // Get UI settings
    const userScale = parseFloat(document.getElementById('sliceVectorScale')?.value) || 1.0;
    const colormap = document.getElementById('sliceVectorColormap')?.value || 'jet';
    const vectorMode = document.getElementById('sliceVectorMode')?.value || '3d'; // '3d' or 'projected'

    console.log(`[Slice Widget Backend] Creating ${data.vectors.length} velocity vectors (scale: ${userScale}, colormap: ${colormap}, mode: ${vectorMode})`);

    const arrowGroup = new THREE.Group();
    arrowGroup.name = 'slice-vectors';
    arrowGroup.renderOrder = 100; // Render on top of other geometry

    // Get vector magnitude range for scaling
    const vectorRange = data.vector_range || { min: 0, max: 1 };
    const maxMagnitude = vectorRange.max || 1;

    // Calculate appropriate arrow scale based on slice size and user scale
    // Use a reasonable default if planeSize is not set
    const effectivePlaneSize = this.planeSize || 10;
    const baseArrowLength = effectivePlaneSize * 0.05 * userScale; // 5% of plane size as base
    const maxArrowLength = effectivePlaneSize * 0.15 * userScale;  // 15% of plane size as max

    console.log(`[Slice Widget Backend] Arrow sizing: planeSize=${this.planeSize}, effectivePlaneSize=${effectivePlaneSize}, baseArrowLength=${baseArrowLength}, maxArrowLength=${maxArrowLength}, maxMagnitude=${maxMagnitude}`);

    // Log first few vectors and positions for debugging
    if (data.vectors.length > 0) {
      console.log(`[Slice Widget Backend] First vector: pos=${JSON.stringify(data.vector_positions[0])}, vec=${JSON.stringify(data.vectors[0])}`);
    }

    // Create arrows for each vector
    for (let i = 0; i < data.vectors.length; i++) {
      const vec = data.vectors[i];
      const pos = data.vector_positions[i];

      if (!vec || !pos) continue;

      const velocity = new THREE.Vector3(vec[0], vec[1], vec[2]);
      const position = new THREE.Vector3(pos[0], pos[1], pos[2]);

      let direction, magnitude;

      if (vectorMode === 'projected') {
        // Project velocity onto the slice plane (remove component normal to plane)
        // This shows only the in-plane velocity components in world space
        const projectedVel = velocity.clone().sub(
          this.planeNormal.clone().multiplyScalar(velocity.dot(this.planeNormal))
        );
        magnitude = projectedVel.length();
        if (magnitude < 1e-6) continue; // Skip zero vectors
        direction = projectedVel.normalize();
      } else {
        // 3D mode: Show velocity vectors in their true world-space direction
        // The arrows point in the actual direction the fluid is flowing
        magnitude = velocity.length();
        if (magnitude < 1e-6) continue; // Skip zero vectors
        direction = velocity.clone().normalize();
      }

      // Scale arrow length based on magnitude
      const normalizedMag = magnitude / maxMagnitude;
      const arrowLength = baseArrowLength + (maxArrowLength - baseArrowLength) * normalizedMag;

      // Color based on magnitude using selected colormap
      const color = this.getVectorColorFromColormap(normalizedMag, colormap, 'sliceVectorColormap');

      const arrow = new THREE.ArrowHelper(
        direction,
        position,
        arrowLength,
        color,
        arrowLength * 0.25,  // Head length (25% of arrow)
        arrowLength * 0.15   // Head width (15% of arrow)
      );

      // Ensure arrow materials are visible and render on top
      arrow.traverse((child) => {
        if (child.material) {
          child.material.depthTest = true;
          child.material.depthWrite = true;
          child.material.transparent = false;
          child.renderOrder = 100; // Render on top
        }
      });

      // Log first arrow details for debugging
      if (i === 0) {
        console.log(`[Slice Widget Backend] First arrow: pos=(${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}), dir=(${direction.x.toFixed(2)}, ${direction.y.toFixed(2)}, ${direction.z.toFixed(2)}), length=${arrowLength.toFixed(4)}, color=0x${color.toString(16)}`);
      }

      arrowGroup.add(arrow);
    }

    // Store reference for cleanup using renderer objects system
    const prefix = this.config.inputPrefix || 'slice';
    const vectorsObjectName = `${prefix}-vectors`;

    console.log(`[Slice Widget Backend] ArrowGroup has ${arrowGroup.children.length} children before adding to scene`);

    // Add to scene using renderer.addObject for consistency with other slice overlays
    if (this.renderer && typeof this.renderer.addObject === 'function') {
      this.renderer.addObject(vectorsObjectName, arrowGroup);
      console.log(`[Slice Widget Backend] Added vectors using renderer.addObject: ${vectorsObjectName}`);
      console.log(`[Slice Widget Backend] Renderer objects now:`, Array.from(this.renderer.objects.keys()));
    } else {
      // Fallback to direct scene add
      this.scene.add(arrowGroup);
      console.log(`[Slice Widget Backend] Added vectors directly to scene (fallback)`);
    }

    // Verify the group is in scene
    console.log(`[Slice Widget Backend] Scene children count: ${this.scene.children.length}`);

    // Store in activeFilters for collaboration sync with all options needed to recreate
    if (this.filterManager && this.filterManager.activeFilters) {
      this.filterManager.activeFilters.set('slice-vectors', {
        type: 'slice_vectors',
        mesh: arrowGroup,
        options: {
          origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
          normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
          vectorField: document.getElementById('sliceVectorField')?.value || 'U',
          vectorScale: parseFloat(document.getElementById('sliceVectorScale')?.value) || 1.0,
          vectorDensity: parseInt(document.getElementById('sliceVectorDensity')?.value) || 200,
          colormap: colormap,
          vectorMode: vectorMode
        }
      });
      console.log('[Slice Widget Backend] Registered slice-vectors filter for collaboration');
      // Note: ViewLock broadcast will happen at the end of updateSliceBackend to avoid intermediate state broadcasts
    }

    console.log(`[Slice Widget Backend] Created ${arrowGroup.children.length} velocity vectors on slice (field: ${data.vector_field || 'unknown'})`);
  }

  /**
   * Get color for vector based on normalized magnitude (0-1)
   * Blue (slow) -> Cyan -> Green -> Yellow -> Red (fast)
   */
  getVectorColor(normalizedMagnitude) {
    const t = Math.max(0, Math.min(1, normalizedMagnitude));

    // Color stops: blue -> cyan -> green -> yellow -> red
    let r, g, b;
    if (t < 0.25) {
      // Blue to Cyan
      const s = t / 0.25;
      r = 0;
      g = s;
      b = 1;
    } else if (t < 0.5) {
      // Cyan to Green
      const s = (t - 0.25) / 0.25;
      r = 0;
      g = 1;
      b = 1 - s;
    } else if (t < 0.75) {
      // Green to Yellow
      const s = (t - 0.5) / 0.25;
      r = s;
      g = 1;
      b = 0;
    } else {
      // Yellow to Red
      const s = (t - 0.75) / 0.25;
      r = 1;
      g = 1 - s;
      b = 0;
    }

    return new THREE.Color(r, g, b);
  }

  /**
   * Get color for vector based on normalized magnitude using a specific colormap
   * @param {number} t - Normalized value (0-1)
   * @param {string} colormapName - Colormap name (jet, viridis, plasma, etc.)
   * @param {string} dropdownId - Optional dropdown ID for solid color lookup
   * @returns {THREE.Color}
   */
  getVectorColorFromColormap(t, colormapName, dropdownId = null) {
    t = Math.max(0, Math.min(1, t));

    // Use centralized Colormap if available
    if (window.Colormap && window.Colormap.getColor) {
      // Get solid color for the specific dropdown if using solid colormap
      const solidColorHex = dropdownId && window.Colormap.isSolidColor(colormapName)
        ? window.Colormap.getSolidColor(dropdownId)
        : null;
      const rgb = window.Colormap.getColor(t, colormapName, solidColorHex);
      return new THREE.Color(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255);
    }

    // Fallback: simple jet colormap
    let r, g, b;
    if (t < 0.25) {
      const s = t / 0.25;
      r = 0; g = s; b = 1;
    } else if (t < 0.5) {
      const s = (t - 0.25) / 0.25;
      r = 0; g = 1; b = 1 - s;
    } else if (t < 0.75) {
      const s = (t - 0.5) / 0.25;
      r = s; g = 1; b = 0;
    } else {
      const s = (t - 0.75) / 0.25;
      r = 1; g = 1 - s; b = 0;
    }

    return new THREE.Color(r, g, b);
  }

  /**
   * Create iso-lines (contour lines) from backend-extracted data (geometry mode)
   * Uses vertices, triangles, and scalars from the slice response
   * Uses THREE.LineSegments2 for proper line width support
   */
  async createSliceIsolinesBackend(data) {
    // Remove any existing isolines first
    this.removeSliceIsolines();

    // Check if we have the required data
    if (!data.vertices || !data.triangles || !data.scalars) {
      console.warn('[Slice Widget Backend] No vertices, triangles, or scalars for isolines');
      Utils.notify('No data available for contour lines', 'warning');
      return;
    }

    // Get UI settings
    const numIsolines = parseInt(document.getElementById('sliceIsolinesCount')?.value) || 10;
    const lineWidth = parseFloat(document.getElementById('sliceIsolinesWidth')?.value) || 2;
    const colormap = document.getElementById('sliceIsolinesColormap')?.value || 'jet';
    const isolineField = document.getElementById('sliceIsolinesField')?.value || '';
    const showLabels = document.getElementById('sliceIsolinesShowLabels')?.checked ?? true;
    const labelScale = parseFloat(document.getElementById('sliceIsolinesLabelScale')?.value) || 1.0;

    console.log(`[Slice Widget Backend] Creating ${numIsolines} iso-lines (lineWidth: ${lineWidth}, colormap: ${colormap}, field: ${isolineField}, showLabels: ${showLabels}, labelScale: ${labelScale})`);

    // Get vertices and scalars
    // Handle both flat TypedArrays (from binary) and nested arrays (from JSON)
    const rawVertices = data.vertices;
    const rawTriangles = data.triangles;
    let scalars = data.scalars;

    // If a different field was selected and available, we'd need to request it
    // For now, use the scalars that came with the slice response
    if (!scalars || scalars.length === 0) {
      console.warn('[Slice Widget Backend] No scalar data for isolines');
      Utils.notify('No scalar field data for contour lines', 'warning');
      return;
    }

    // Detect format: flat TypedArray vs nested array
    const verticesAreFlat = rawVertices instanceof Float32Array ||
                            (Array.isArray(rawVertices) && typeof rawVertices[0] === 'number');
    const trianglesAreFlat = rawTriangles instanceof Uint32Array ||
                             (Array.isArray(rawTriangles) && typeof rawTriangles[0] === 'number');

    // Helper functions to access data in either format
    const getVertex = verticesAreFlat
      ? (idx) => [rawVertices[idx * 3], rawVertices[idx * 3 + 1], rawVertices[idx * 3 + 2]]
      : (idx) => rawVertices[idx];

    const getTriangle = trianglesAreFlat
      ? (idx) => [rawTriangles[idx * 3], rawTriangles[idx * 3 + 1], rawTriangles[idx * 3 + 2]]
      : (idx) => rawTriangles[idx];

    const numTriangles = trianglesAreFlat ? rawTriangles.length / 3 : rawTriangles.length;

    // Get scalar range
    const scalarRange = data.scalar_range || {
      min: Math.min(...scalars),
      max: Math.max(...scalars)
    };
    const minVal = scalarRange.min;
    const maxVal = scalarRange.max;

    // Generate iso-values (evenly spaced, excluding min and max)
    const isoValues = [];
    for (let i = 1; i <= numIsolines; i++) {
      const t = i / (numIsolines + 1);
      isoValues.push(minVal + t * (maxVal - minVal));
    }

    // Create line group
    const prefix = this.config.inputPrefix || 'slice';
    const isolinesObjectName = `${prefix}-isolines`;

    const isolineGroup = new THREE.Group();
    isolineGroup.name = isolinesObjectName;
    isolineGroup.renderOrder = 110; // Render on top of slice mesh

    // Get canvas size for LineMaterial resolution
    const canvas = this.renderer?.renderer?.domElement || document.querySelector('canvas');
    const resolution = canvas ? new THREE.Vector2(canvas.clientWidth, canvas.clientHeight) : new THREE.Vector2(800, 600);

    // For each iso-value, extract contour lines using marching squares on triangles
    for (let isoIdx = 0; isoIdx < isoValues.length; isoIdx++) {
      const isoValue = isoValues[isoIdx];
      const lineSegments = [];

      // Process each triangle to find edge crossings
      for (let triIdx = 0; triIdx < numTriangles; triIdx++) {
        const tri = getTriangle(triIdx);
        if (!tri || tri.length < 3) continue;

        const i0 = tri[0], i1 = tri[1], i2 = tri[2];

        // Get vertices and scalar values for this triangle
        const v0 = getVertex(i0), v1 = getVertex(i1), v2 = getVertex(i2);
        const s0 = scalars[i0], s1 = scalars[i1], s2 = scalars[i2];

        if (!v0 || !v1 || !v2 || s0 === undefined || s1 === undefined || s2 === undefined) continue;

        // Find edge crossings
        const crossings = [];

        // Edge 0-1
        if ((s0 < isoValue && s1 >= isoValue) || (s0 >= isoValue && s1 < isoValue)) {
          const t = (isoValue - s0) / (s1 - s0);
          crossings.push([
            v0[0] + t * (v1[0] - v0[0]),
            v0[1] + t * (v1[1] - v0[1]),
            v0[2] + t * (v1[2] - v0[2])
          ]);
        }

        // Edge 1-2
        if ((s1 < isoValue && s2 >= isoValue) || (s1 >= isoValue && s2 < isoValue)) {
          const t = (isoValue - s1) / (s2 - s1);
          crossings.push([
            v1[0] + t * (v2[0] - v1[0]),
            v1[1] + t * (v2[1] - v1[1]),
            v1[2] + t * (v2[2] - v1[2])
          ]);
        }

        // Edge 2-0
        if ((s2 < isoValue && s0 >= isoValue) || (s2 >= isoValue && s0 < isoValue)) {
          const t = (isoValue - s2) / (s0 - s2);
          crossings.push([
            v2[0] + t * (v0[0] - v2[0]),
            v2[1] + t * (v0[1] - v2[1]),
            v2[2] + t * (v0[2] - v2[2])
          ]);
        }

        // If we have exactly 2 crossings, we have a line segment
        if (crossings.length === 2) {
          lineSegments.push(crossings[0], crossings[1]);
        }
      }

      if (lineSegments.length === 0) continue;

      // Debug: log first line segment coordinates for first iso-value
      if (isoIdx === 0 && lineSegments.length >= 2) {
        const p1 = lineSegments[0], p2 = lineSegments[1];
        console.log(`[Slice Widget Backend] First isoline segment: p1=[${p1[0].toFixed(3)}, ${p1[1].toFixed(3)}, ${p1[2].toFixed(3)}], p2=[${p2[0].toFixed(3)}, ${p2[1].toFixed(3)}, ${p2[2].toFixed(3)}], isoValue=${isoValue.toFixed(3)}`);
      }

      // Color based on iso-value position in range
      const normalizedValue = (isoValue - minVal) / (maxVal - minVal);
      const color = this.getVectorColorFromColormap(normalizedValue, colormap, 'sliceIsolinesColormap');

      // Use LineSegments2 for fat lines (proper line width support)
      let line;
      if (typeof THREE.LineSegmentsGeometry !== 'undefined' && typeof THREE.LineMaterial !== 'undefined') {
        // Fat lines with variable width
        const geometry = new THREE.LineSegmentsGeometry();
        const positions = lineSegments.flat();
        geometry.setPositions(positions);

        const material = new THREE.LineMaterial({
          color: color,
          linewidth: lineWidth, // In pixels
          resolution: resolution,
          depthTest: true,
          depthWrite: true
        });

        line = new THREE.LineSegments2(geometry, material);
      } else {
        // Fallback to basic lines (width=1)
        console.warn('[Slice Widget Backend] LineSegments2 not available, using basic lines');
        const lineGeometry = new THREE.BufferGeometry();
        const positions = new Float32Array(lineSegments.flat());
        lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        const lineMaterial = new THREE.LineBasicMaterial({
          color: color,
          transparent: false,
          depthTest: true,
          depthWrite: true
        });

        line = new THREE.LineSegments(lineGeometry, lineMaterial);
      }

      line.renderOrder = 110;

      // No offset - lines are placed directly on the slice plane
      isolineGroup.add(line);

      // Add label for this iso-line if enabled
      if (showLabels && lineSegments.length > 0) {
        // Find a good position for the label (use midpoint of middle segment)
        const midSegmentIdx = Math.floor(lineSegments.length / 4) * 2; // Pick segment near middle
        const labelPos = new THREE.Vector3(
          (lineSegments[midSegmentIdx][0] + lineSegments[midSegmentIdx + 1][0]) / 2,
          (lineSegments[midSegmentIdx][1] + lineSegments[midSegmentIdx + 1][1]) / 2,
          (lineSegments[midSegmentIdx][2] + lineSegments[midSegmentIdx + 1][2]) / 2
        );

        // Create text sprite label with user-defined scale factor
        const label = this.createIsolineLabel(isoValue, color, labelPos, labelScale);
        if (label) {
          isolineGroup.add(label);
        }
      }
    }

    // Add to scene
    if (this.renderer && typeof this.renderer.addObject === 'function') {
      this.renderer.addObject(isolinesObjectName, isolineGroup);
    } else {
      this.scene.add(isolineGroup);
    }

    // Store in activeFilters for collaboration sync with all options needed to recreate
    if (this.filterManager && this.filterManager.activeFilters) {
      this.filterManager.activeFilters.set('slice-isolines', {
        type: 'slice_isolines',
        mesh: isolineGroup,
        options: {
          origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
          normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
          isolinesField: document.getElementById('sliceIsolinesField')?.value || '',
          numIsolines: numIsolines,
          lineWidth: lineWidth,
          colormap: colormap,
          showLabels: showLabels,
          labelScale: labelScale,
          scalarRange: { min: minVal, max: maxVal }
        }
      });
    }

    // Update the isolines legend
    if (this.viewer) {
      this.viewer.updateIsolinesLegend('slice-isolines', {
        field: isolineField || 'pressure',
        colormap: colormap,
        scalarRange: { min: minVal, max: maxVal },
        numIsolines: numIsolines
      });
    }
  }

  /**
   * Create a text sprite label for an iso-line value
   * Labels are small and positioned on the line
   * @param {number} value - The iso-value to display
   * @param {number} color - The color for the label
   * @param {THREE.Vector3} position - Position for the label
   * @param {number} userScaleFactor - User-defined scale multiplier (default 1.0)
   */
  createIsolineLabel(value, color, position, userScaleFactor = 1.0) {
    // Format the value (use scientific notation for very large/small numbers)
    let labelText;
    if (Math.abs(value) >= 10000 || (Math.abs(value) < 0.01 && value !== 0)) {
      labelText = value.toExponential(1);
    } else if (Math.abs(value) >= 100) {
      labelText = value.toFixed(0);
    } else if (Math.abs(value) >= 1) {
      labelText = value.toFixed(1);
    } else {
      labelText = value.toFixed(2);
    }

    // Create canvas for text-only label (no background)
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 24;

    // Clear canvas (transparent background)
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw text with outline for visibility
    context.font = 'bold 14px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Use the iso-line color for the text
    const colorObj = new THREE.Color(color);
    const textColor = `rgb(${Math.round(colorObj.r * 255)}, ${Math.round(colorObj.g * 255)}, ${Math.round(colorObj.b * 255)})`;

    // Draw dark outline for contrast
    context.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    context.lineWidth = 3;
    context.strokeText(labelText, canvas.width / 2, canvas.height / 2);

    // Draw colored text on top
    context.fillStyle = textColor;
    context.fillText(labelText, canvas.width / 2, canvas.height / 2);

    // Create texture and sprite
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;

    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: true,
      depthWrite: false
    });

    const sprite = new THREE.Sprite(spriteMaterial);

    // Position the sprite
    sprite.position.copy(position);

    // Scale the sprite based on actual plane dimensions
    // Use the larger of width/height to determine readable label size
    const effectivePlaneSize = Math.max(this.planeWidth || 1, this.planeHeight || 1);
    // Base scale: ~3% of the plane size, multiplied by user scale factor
    const baseScale = effectivePlaneSize * 0.03 * userScaleFactor;
    sprite.scale.set(baseScale, baseScale * 0.4, 1);

    sprite.renderOrder = 120; // Render on top of lines

    return sprite;
  }

  /**
   * Remove velocity vectors from slice
   */
  removeSliceVectors() {
    const prefix = this.config.inputPrefix || 'slice';
    const vectorsObjectName = `${prefix}-vectors`;

    console.log(`[Slice Widget Backend] removeSliceVectors called, looking for: ${vectorsObjectName}`);

    // Remove using renderer.removeObject
    if (this.renderer && typeof this.renderer.removeObject === 'function') {
      this.renderer.removeObject(vectorsObjectName);
      console.log(`[Slice Widget Backend] Called renderer.removeObject for: ${vectorsObjectName}`);
    }

    // Also search scene directly for vector groups - only remove THIS widget's vectors
    if (this.scene) {
      const toRemove = [];
      this.scene.traverse((child) => {
        // Only remove this widget's vectors (matching the prefix), not other widgets' vectors
        if (child.name === vectorsObjectName) {
          toRemove.push(child);
        }
      });

      console.log(`[Slice Widget Backend] Found ${toRemove.length} vector groups to remove from scene`);

      for (const obj of toRemove) {
        console.log(`[Slice Widget Backend] Removing vector group: ${obj.name}`);
        if (obj.parent) {
          obj.parent.remove(obj);
        } else {
          this.scene.remove(obj);
        }
        // Dispose resources
        obj.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    }

    // Also remove from activeFilters for collaboration sync
    if (this.filterManager && this.filterManager.activeFilters) {
      this.filterManager.activeFilters.delete('slice-vectors');
    }

    // Broadcast state change to sync filter removal with followers
    if (window.viewer?.viewLockManager) {
      window.viewer.viewLockManager.onVisualizationChange(true);
    }
  }

  createSliceIsolines(data, variable) {
    // Create contour lines (iso-lines) on the slice
    const sliceMesh = this.filterManager.activeFilters.get('slice-filter')?.mesh;
    if (!sliceMesh || !sliceMesh.geometry) return;

    const scalars = data.fields[variable];
    if (!scalars) return;

    // Get scalar range
    const min = Math.min(...scalars);
    const max = Math.max(...scalars);

    // Create 5-7 iso-lines across the range
    const numIsolines = 7;
    const isolineGroup = new THREE.Group();
    isolineGroup.name = 'slice-isolines';

    for (let i = 1; i < numIsolines; i++) {
      const isoValue = min + (max - min) * (i / numIsolines);

      // Extract contour at this iso-value
      // This is simplified - full implementation would use marching squares
      // For now, create line segments where scalar crosses isoValue
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0xffffff,
        linewidth: 2,
        opacity: 0.6,
        transparent: true
      });

      // Add to group (actual geometry extraction would go here)
      // For production, use conrec.js or similar contouring library
    }

    this.scene.add(isolineGroup);

    // Store in activeFilters for collaboration sync with all options needed to recreate
    this.filterManager.activeFilters.set('slice-isolines', {
      type: 'slice_isolines',
      mesh: isolineGroup,
      options: {
        origin: [this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z],
        normal: [this.planeNormal.x, this.planeNormal.y, this.planeNormal.z],
        isolinesField: variable,
        numIsolines: numIsolines,
        lineWidth: 2,
        colormap: document.getElementById('sliceIsolinesColormap')?.value || 'jet',
        showLabels: document.getElementById('sliceIsolinesShowLabels')?.checked || false,
        scalarRange: { min: min, max: max }
      }
    });
    // Note: ViewLock broadcast happens at the end of updateSlice to avoid intermediate state broadcasts

    console.log(`Created ${numIsolines} iso-lines on slice`);
  }

  // Helper: Sample points uniformly from the slice mesh vertices
  generateSeedPointsOnPlane(count) {
    // Generate seed points on a regular grid in the slice plane
    // This is used for backend mode where we don't have slice mesh vertices

    const bbox = this.getBoundingBoxFromScene();
    if (!bbox) {
      console.error('[generateSeedPointsOnPlane] No bounding box available');
      return [];
    }

    // Create two orthogonal vectors in the slice plane
    const normal = this.planeNormal.clone().normalize();

    // Find first tangent vector (perpendicular to normal)
    let tangent1 = new THREE.Vector3();
    if (Math.abs(normal.x) < 0.9) {
      tangent1.crossVectors(normal, new THREE.Vector3(1, 0, 0)).normalize();
    } else {
      tangent1.crossVectors(normal, new THREE.Vector3(0, 1, 0)).normalize();
    }

    // Second tangent vector (perpendicular to both normal and tangent1)
    const tangent2 = new THREE.Vector3();
    tangent2.crossVectors(normal, tangent1).normalize();

    // Determine grid size based on mesh bounds
    const size = bbox.size;
    const planeExtent = Math.max(size.x, size.y, size.z) * 0.8; // 80% of mesh size

    // Calculate grid dimensions
    const gridSize = Math.ceil(Math.sqrt(count));
    const spacing = planeExtent / (gridSize - 1);

    const seedPoints = [];
    const halfExtent = planeExtent / 2;

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const u = -halfExtent + i * spacing;
        const v = -halfExtent + j * spacing;

        // Compute 3D position on the plane
        const point = this.planeOrigin.clone();
        point.addScaledVector(tangent1, u);
        point.addScaledVector(tangent2, v);

        seedPoints.push([point.x, point.y, point.z]);
      }
    }

    console.log(`[generateSeedPointsOnPlane] Generated ${seedPoints.length} points on ${gridSize}x${gridSize} grid`);
    return seedPoints;
  }

  // Helper: Generate seed points along a line on the slice plane
  // This provides perfect natural spreading without clustering
  // rotation: angle in degrees (0-180)
  // offset: perpendicular offset as percentage (-100 to +100)
  generateSeedPointsOnPlaneWithinMesh(count, rotation = 45, offset = 0) {
    const bbox = this.getBoundingBoxFromScene();
    if (!bbox) {
      console.error('[generateSeedPointsOnPlaneWithinMesh] No bounding box available');
      return [];
    }

    // Create two orthogonal vectors in the slice plane
    const normal = this.planeNormal.clone().normalize();

    // Find first tangent vector (perpendicular to normal)
    let tangent1 = new THREE.Vector3();
    if (Math.abs(normal.x) < 0.9) {
      tangent1.crossVectors(normal, new THREE.Vector3(1, 0, 0)).normalize();
    } else {
      tangent1.crossVectors(normal, new THREE.Vector3(0, 1, 0)).normalize();
    }

    // Second tangent vector (perpendicular to both normal and tangent1)
    const tangent2 = new THREE.Vector3();
    tangent2.crossVectors(normal, tangent1).normalize();

    // Determine plane extent based on mesh bounds
    const size = bbox.size;
    const planeExtent = Math.max(size.x, size.y, size.z) * 1.2;
    const halfExtent = planeExtent / 2;

    // Convert rotation to radians
    const angleRad = (rotation * Math.PI) / 180;

    // Calculate line direction and perpendicular in plane coordinates
    const dirU = Math.cos(angleRad);
    const dirV = Math.sin(angleRad);
    const perpU = -Math.sin(angleRad);
    const perpV = Math.cos(angleRad);

    // Calculate offset in plane coordinates (as fraction of extent)
    const offsetFraction = offset / 100; // Convert percentage to fraction
    const offsetU = perpU * offsetFraction * halfExtent;
    const offsetV = perpV * offsetFraction * halfExtent;

    // Sample along the line
    const seedPoints = [];
    const lineLength = planeExtent * Math.sqrt(2); // Diagonal length

    for (let i = 0; i < count; i++) {
      // Sample uniformly along the line
      const t = (i / (count - 1)) - 0.5; // -0.5 to 0.5
      const dist = t * lineLength;

      // Position along the line direction, plus offset
      const u = dist * dirU + offsetU;
      const v = dist * dirV + offsetV;

      // Compute 3D position on the plane
      const point = this.planeOrigin.clone();
      point.addScaledVector(tangent1, u);
      point.addScaledVector(tangent2, v);

      seedPoints.push([point.x, point.y, point.z]);
    }

    console.log(`[generateSeedPointsOnPlaneWithinMesh] Generated ${seedPoints.length} points (rotation: ${rotation}°, offset: ${offset}%)`);
    return seedPoints;
  }

  // Helper: Update the visual preview of the seed line
  updateSeedLinePreview() {
    // Only show preview in backend mode and when streamlines are enabled AND settings are visible
    const streamlinesEnabled = document.getElementById('sliceShowStreamlines')?.checked;
    const streamlineSettings = document.getElementById('sliceStreamlineSettings');
    const settingsVisible = streamlineSettings && streamlineSettings.style.display !== 'none';

    // Don't show seed line preview when just viewing heatmap - only when actively working with streamlines
    if (this.viewer.renderingMode !== 'geometry' || !streamlinesEnabled || !settingsVisible) {
      // Remove preview if it exists (use prefix to avoid conflicts)
      if (this.seedLinePreview) {
        const prefix = this.config.inputPrefix;
        const seedLinePreviewName = `${prefix}-seed-line-preview`;
        this.renderer.removeObject(seedLinePreviewName);
        this.seedLinePreview = null;
      }
      return;
    }

    const bbox = this.getBoundingBoxFromScene();
    if (!bbox) return;

    // Get rotation and offset from UI
    const rotationInput = document.getElementById('sliceStreamlineRotation');
    const offsetInput = document.getElementById('sliceStreamlineOffset');
    const rotation = rotationInput ? parseFloat(rotationInput.value) : 45;
    const offset = offsetInput ? parseFloat(offsetInput.value) : 0;

    // Create two orthogonal vectors in the slice plane
    const normal = this.planeNormal.clone().normalize();

    let tangent1 = new THREE.Vector3();
    if (Math.abs(normal.x) < 0.9) {
      tangent1.crossVectors(normal, new THREE.Vector3(1, 0, 0)).normalize();
    } else {
      tangent1.crossVectors(normal, new THREE.Vector3(0, 1, 0)).normalize();
    }

    const tangent2 = new THREE.Vector3();
    tangent2.crossVectors(normal, tangent1).normalize();

    // Calculate line parameters (same as seed generation)
    const size = bbox.size;
    const planeExtent = Math.max(size.x, size.y, size.z) * 1.2;
    const halfExtent = planeExtent / 2;

    const angleRad = (rotation * Math.PI) / 180;
    const dirU = Math.cos(angleRad);
    const dirV = Math.sin(angleRad);
    const perpU = -Math.sin(angleRad);
    const perpV = Math.cos(angleRad);

    const offsetFraction = offset / 100;
    const offsetU = perpU * offsetFraction * halfExtent;
    const offsetV = perpV * offsetFraction * halfExtent;

    const lineLength = planeExtent * Math.sqrt(2);

    // Create line endpoints
    const startT = -0.5;
    const endT = 0.5;

    const startU = startT * lineLength * dirU + offsetU;
    const startV = startT * lineLength * dirV + offsetV;
    const endU = endT * lineLength * dirU + offsetU;
    const endV = endT * lineLength * dirV + offsetV;

    const startPoint = this.planeOrigin.clone();
    startPoint.addScaledVector(tangent1, startU);
    startPoint.addScaledVector(tangent2, startV);

    const endPoint = this.planeOrigin.clone();
    endPoint.addScaledVector(tangent1, endU);
    endPoint.addScaledVector(tangent2, endV);

    // Create a group for the line and indicator
    const previewGroup = new THREE.Group();

    // Create a thick tube for the line (more visible than basic line)
    const linePath = new THREE.LineCurve3(startPoint, endPoint);
    const tubeGeometry = new THREE.TubeGeometry(linePath, 2, size.length() * 0.005, 8, false);
    const tubeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      opacity: 0.9,
      transparent: true
    });
    const tubeLine = new THREE.Mesh(tubeGeometry, tubeMaterial);
    previewGroup.add(tubeLine);

    // Add a blinking sphere at the center of the line
    const centerPoint = new THREE.Vector3().addVectors(startPoint, endPoint).multiplyScalar(0.5);
    const sphereGeometry = new THREE.SphereGeometry(size.length() * 0.015, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      opacity: 1.0,
      transparent: true
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.position.copy(centerPoint);
    previewGroup.add(sphere);

    // Store the sphere for blinking animation
    this.seedLineIndicator = sphere;

    // Add to scene (use prefix to avoid conflicts between widgets)
    const prefix = this.config.inputPrefix;
    const seedLinePreviewName = `${prefix}-seed-line-preview`;
    this.renderer.removeObject(seedLinePreviewName);
    this.renderer.addObject(seedLinePreviewName, previewGroup);
    this.seedLinePreview = previewGroup;
  }

  // Start blinking animation for the seed line indicator
  startSeedLineBlinking() {
    if (this.seedLineBlinkInterval) {
      return; // Already blinking
    }

    if (!this.seedLineIndicator) {
      return;
    }

    // Blink every 300ms
    let visible = true;
    this.seedLineBlinkInterval = setInterval(() => {
      if (this.seedLineIndicator) {
        visible = !visible;
        this.seedLineIndicator.material.opacity = visible ? 1.0 : 0.2;
      }
    }, 300);
  }

  // Stop blinking animation
  stopSeedLineBlinking() {
    if (this.seedLineBlinkInterval) {
      clearInterval(this.seedLineBlinkInterval);
      this.seedLineBlinkInterval = null;
    }

    // Reset to full opacity
    if (this.seedLineIndicator) {
      this.seedLineIndicator.material.opacity = 1.0;
    }
  }

  // Populate slice streamline field dropdowns
  populateSliceStreamlineFields() {
    console.log('[populateSliceStreamlineFields] Called');
    console.log('[populateSliceStreamlineFields] renderingMode:', this.viewer?.renderingMode);

    // In backend mode, use availableFields list; in frontend mode, use fields data
    let fieldNames = [];
    let fields = null;

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateSliceStreamlineFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateSliceStreamlineFields] Frontend mode - fields:', fields);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateSliceStreamlineFields] No fields available');
      return;
    }

    // Populate vector field dropdown (for integration)
    const vectorFieldSelect = document.getElementById('sliceStreamlineVectorField');
    if (vectorFieldSelect) {
      const previousValue = vectorFieldSelect.value || '';
      vectorFieldSelect.innerHTML = '';

      // Find vector fields
      let vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - use availableVectorFields from backend (actual 3-component fields)
        vectorFields = this.viewer?.availableVectorFields || [];
        console.log('[populateSliceStreamlineFields] Backend mode - using availableVectorFields:', vectorFields);
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      console.log('[populateSliceStreamlineFields] vectorFields:', vectorFields);

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        vectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && vectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (vectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      vectorFieldSelect.value = preferred;
      console.log('[populateSliceStreamlineFields] Vector field set to:', preferred);
    }

    // Populate color field dropdown (for visualization - ALL fields)
    const colorFieldSelect = document.getElementById('sliceStreamlineColorField');
    if (colorFieldSelect) {
      const previousValue = colorFieldSelect.value || '';
      colorFieldSelect.innerHTML = '';

      // Add Auto option
      const autoOption = document.createElement('option');
      autoOption.value = '';
      autoOption.textContent = 'Auto';
      colorFieldSelect.appendChild(autoOption);

      // Separate scalar and vector fields
      const scalarFields = [];
      let vectorFieldsForColor = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - use availableVectorFields and determine scalar fields by exclusion
        vectorFieldsForColor = this.viewer?.availableVectorFields || [];
        fieldNames.forEach(name => {
          if (!vectorFieldsForColor.includes(name)) {
            scalarFields.push(name);
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFieldsForColor.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      }

      console.log('[populateSliceStreamlineFields] Color dropdown - scalarFields:', scalarFields, 'vectorFields:', vectorFieldsForColor);

      // Add scalar fields first (more common for coloring)
      if (scalarFields.length > 0) {
        const scalarGroup = document.createElement('optgroup');
        scalarGroup.label = 'Scalar Fields';
        scalarFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          scalarGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(scalarGroup);
      }

      // Add vector fields
      if (vectorFieldsForColor.length > 0) {
        const vectorGroup = document.createElement('optgroup');
        vectorGroup.label = 'Vector Fields';
        vectorFieldsForColor.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          vectorGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(vectorGroup);
      }

      // Set preferred value
      let preferred = '';
      if (previousValue && colorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (colorFieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (colorFieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (colorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      }
      colorFieldSelect.value = preferred;
      console.log('[populateSliceStreamlineFields] Color field set to:', preferred);
      console.log('[populateSliceStreamlineFields] Final dropdown has', colorFieldSelect.options.length, 'options');
    }
  }

  // Populate slice contour field dropdown
  populateSliceContourFields() {
    console.log('[populateSliceContourFields] Called');

    // In backend mode, use availableFields list; in frontend mode, use fields data
    let fieldNames = [];

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateSliceContourFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      const fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateSliceContourFields] Frontend mode - fields:', fieldNames);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateSliceContourFields] No fields available');
      return;
    }

    // Populate field dropdown
    const fieldSelect = document.getElementById('sliceContourField');
    if (fieldSelect) {
      const previousValue = fieldSelect.value || '';
      fieldSelect.innerHTML = '<option value="">Select field...</option>';

      // Add all fields (both scalar and vector - backend will handle magnitude for vectors)
      fieldNames.sort((a, b) => a.localeCompare(b)).forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        fieldSelect.appendChild(option);
      });

      // Set default - prefer pressure, then velocity magnitude
      let preferred = '';
      if (previousValue && fieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (fieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (fieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (fieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (fieldNames.length > 0) {
        preferred = fieldNames[0];
      }
      fieldSelect.value = preferred;
      console.log('[populateSliceContourFields] Field set to:', preferred);
    }
  }

  /**
   * Populate the vector field dropdown for velocity vectors
   */
  populateSliceVectorFields() {
    console.log('[populateSliceVectorFields] Called');

    // Get available vector fields (3-component fields)
    let vectorFields = [];

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableVectorFields from geometry response
      vectorFields = this.viewer?.availableVectorFields || [];
      console.log('[populateSliceVectorFields] Backend mode - availableVectorFields:', vectorFields);
    } else {
      // Frontend mode - get from currentData.fields
      const fields = this.viewer?.currentData?.fields || {};
      console.log('[populateSliceVectorFields] Frontend mode - fields:', Object.keys(fields));

      // Find fields that are arrays of 3-component vectors
      for (const [name, data] of Object.entries(fields)) {
        if (Array.isArray(data) && data.length > 0) {
          const first = data[0];
          if (Array.isArray(first) && first.length === 3) {
            vectorFields.push(name);
          }
        }
      }
    }

    console.log('[populateSliceVectorFields] Vector fields found:', vectorFields);

    // Populate the dropdown
    const fieldSelect = document.getElementById('sliceVectorField');
    if (fieldSelect) {
      // Clear existing options
      fieldSelect.innerHTML = '';

      if (vectorFields.length === 0) {
        fieldSelect.innerHTML = '<option value="">No vector fields available</option>';
        console.warn('[populateSliceVectorFields] No vector fields available');
        return;
      }

      // Add options for each vector field
      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        // Add friendly label for common fields
        if (name === 'U') {
          option.textContent = 'U (Velocity)';
        } else if (name === 'U_mean') {
          option.textContent = 'U_mean (Mean Velocity)';
        } else {
          option.textContent = name;
        }
        fieldSelect.appendChild(option);
      });

      // Try to select a preferred field
      let preferred = '';
      if (fieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (fieldSelect.querySelector('option[value="velocity"]')) {
        preferred = 'velocity';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      fieldSelect.value = preferred;
      console.log('[populateSliceVectorFields] Field set to:', preferred);
    }
  }

  // Remove slice colored contours
  removeSliceContours() {
    const prefix = this.config.inputPrefix || 'slice';
    const slicePlaneObjectName = `${prefix}-plane`;

    // Remove the slice mesh from the scene
    if (this.renderer) {
      this.renderer.removeObject(slicePlaneObjectName);
      console.log('[Slice Widget] Removed slice contours:', slicePlaneObjectName);
    }

    // Also remove from active filters for collaboration
    if (this.filterManager && this.filterManager.activeFilters) {
      this.filterManager.activeFilters.delete('slice-contours');
      console.log('[Slice Widget] Removed slice-contours from active filters');
    }

    // Hide the slice legend
    if (this.viewer) {
      this.viewer.hideSliceLegend();
    }

    // Broadcast state change to sync filter removal with followers
    if (window.viewer?.viewLockManager) {
      console.log('[Slice Widget] Broadcasting filter removal to followers');
      window.viewer.viewLockManager.onVisualizationChange(true);
    }
  }

  // Populate slice isolines field dropdown
  populateSliceIsolinesFields() {
    console.log('[populateSliceIsolinesFields] Called');

    // In backend mode, use availableFields list; in frontend mode, use fields data
    let fieldNames = [];

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateSliceIsolinesFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      const fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateSliceIsolinesFields] Frontend mode - fields:', fieldNames);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateSliceIsolinesFields] No fields available');
      return;
    }

    // Populate field dropdown
    const fieldSelect = document.getElementById('sliceIsolinesField');
    if (fieldSelect) {
      const previousValue = fieldSelect.value || '';
      fieldSelect.innerHTML = '<option value="">Select field...</option>';

      // Add all fields (both scalar and vector - we'll compute magnitude for vectors)
      fieldNames.sort((a, b) => a.localeCompare(b)).forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        fieldSelect.appendChild(option);
      });

      // Set default - prefer pressure, then velocity magnitude
      let preferred = '';
      if (previousValue && fieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (fieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (fieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (fieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (fieldNames.length > 0) {
        preferred = fieldNames[0];
      }
      fieldSelect.value = preferred;
      console.log('[populateSliceIsolinesFields] Field set to:', preferred);
    }
  }

  // Remove slice isolines (contour lines)
  removeSliceIsolines() {
    const prefix = this.config.inputPrefix || 'slice';
    const isolinesObjectName = `${prefix}-isolines`;

    // Remove isolines from the scene
    if (this.scene) {
      const toRemove = [];
      this.scene.traverse(child => {
        if (child.name === isolinesObjectName) {
          toRemove.push(child);
        }
      });
      toRemove.forEach(obj => {
        this.scene.remove(obj);
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(m => m.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      if (toRemove.length > 0) {
        console.log('[Slice Widget] Removed slice isolines:', isolinesObjectName);
      }
    }

    // Also remove from active filters
    if (this.filterManager && this.filterManager.activeFilters) {
      this.filterManager.activeFilters.delete('slice-isolines');
      console.log('[Slice Widget] Removed slice-isolines from active filters');
    }

    // Hide the isolines legend
    if (this.viewer && typeof this.viewer.hideIsolinesLegend === 'function') {
      this.viewer.hideIsolinesLegend();
    }
  }

  /**
   * Utility method to run an async operation with a progress bar in a button
   * @param {string} buttonId - The ID of the button element
   * @param {string} progressBarId - Unique ID for the progress bar element
   * @param {Function} asyncFn - The async function to run
   * @param {Object} options - Options for messages
   */
  async runWithProgressBar(buttonId, progressBarId, asyncFn, options = {}) {
    const btn = document.getElementById(buttonId);
    if (!btn) {
      console.error(`[runWithProgressBar] Button not found: ${buttonId}`);
      return;
    }

    const {
      startMessage = 'Processing...',
      successMessage = 'Complete',
      errorPrefix = 'Error'
    } = options;

    const originalText = 'Apply';
    btn.disabled = true;
    btn.innerHTML = `
      <div style="display: flex; align-items: center; gap: 6px; width: 100%;">
        <div style="flex: 1; height: 6px; background: rgba(0,0,0,0.2); border-radius: 3px; overflow: hidden;">
          <div id="${progressBarId}" style="height: 100%; width: 0%; background: linear-gradient(90deg, #4CAF50, #8BC34A); transition: width 0.2s ease;"></div>
        </div>
        <span id="${progressBarId}Text" style="font-size: 10px; min-width: 28px;">0%</span>
      </div>
    `;

    let progress = 0;
    let progressInterval = setInterval(() => {
      if (progress < 90) {
        progress += Math.random() * 10;
        if (progress > 90) progress = 90;
        const bar = document.getElementById(progressBarId);
        const text = document.getElementById(`${progressBarId}Text`);
        if (bar) bar.style.width = `${progress}%`;
        if (text) text.textContent = `${Math.round(progress)}%`;
      }
    }, 250);

    const restoreButton = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      btn.innerHTML = originalText;
      btn.disabled = false;
    };

    try {
      Utils.notify(startMessage, 'info');

      await asyncFn();

      // Show 100% briefly before restoring
      clearInterval(progressInterval);
      progressInterval = null;
      const bar = document.getElementById(progressBarId);
      const text = document.getElementById(`${progressBarId}Text`);
      if (bar) bar.style.width = '100%';
      if (text) text.textContent = '100%';

      Utils.notify(successMessage, 'success');

      // Restore button after brief delay to show 100%
      setTimeout(restoreButton, 500);
    } catch (error) {
      console.error(`[runWithProgressBar] ${errorPrefix}:`, error);
      Utils.notify(`${errorPrefix}: ${error.message || error}`, 'error');
      restoreButton();
    }
  }

  samplePointsOnSlice(mesh, data, count) {
    const geometry = mesh.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    const positionAttr = geometry.getAttribute('position');
    if (!positionAttr) {
      console.error('❌ No position attribute in slice geometry');
      return [];
    }

    const desired = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    if (desired <= 0) {
      return [];
    }

    const normal = this.planeNormal.clone();
    if (!Number.isFinite(normal.x) || !Number.isFinite(normal.y) || !Number.isFinite(normal.z) || normal.lengthSq() < 1e-12) {
      normal.set(0, 0, 1);
    } else {
      normal.normalize();
    }

    const axes = typeof this.filterManager?.computePlaneAxes === 'function'
      ? this.filterManager.computePlaneAxes(normal)
      : null;
    const axisU = axes?.axisU?.clone()?.normalize() ?? null;
    const axisV = axes?.axisV?.clone()?.normalize() ?? null;

    const seeds = [];

    if (axisU && axisV) {
      const boundsSeeds = this.sampleSeedsFromGridBounds(data, axisU, axisV, normal, desired);
      if (boundsSeeds.length > 0) {
        seeds.push(...boundsSeeds);
      }
    }

    let remaining = desired - seeds.length;
    if (remaining > 0) {
      const triangleSeeds = this.sampleSeedsFromSliceTriangles(geometry, remaining);
      if (triangleSeeds.length > 0) {
        seeds.push(...triangleSeeds);
      }
    }

    remaining = desired - seeds.length;
    if (remaining > 0) {
      const fallbackSeeds = this.samplePointsFallback(positionAttr, remaining);
      seeds.push(...fallbackSeeds);
    }

    if (seeds.length === 0) {
      console.warn('❌ No seeds could be generated on slice');
      return [];
    }

    const tolerance = 1e-6;
    const dedupe = new Map();
    seeds.forEach(pt => {
      if (!Array.isArray(pt) || pt.length < 3) return;
      const key = `${Math.round(pt[0] / tolerance) * tolerance}|${Math.round(pt[1] / tolerance) * tolerance}|${Math.round(pt[2] / tolerance) * tolerance}`;
      if (!dedupe.has(key)) {
        dedupe.set(key, [pt[0], pt[1], pt[2]]);
      }
    });

    const uniqueSeeds = Array.from(dedupe.values());
    if (uniqueSeeds.length === 0) {
      console.warn('❌ All slice seed samples collapsed to duplicates');
      return [];
    }

    const expandedSeeds = uniqueSeeds.slice();
    if (axisU && axisV && expandedSeeds.length > 0 && expandedSeeds.length < desired) {
      const baseList = uniqueSeeds.slice();
      const spanEstimate = geometry.boundingBox
        ? geometry.boundingBox.getSize(new THREE.Vector3()).length()
        : 1;
      const jitterScale = Math.max(spanEstimate * 1e-3, 5e-4);
      const golden = 0.6180339887498949;
      const silver = 0.7548776662466927;
      let attempt = 0;
      while (expandedSeeds.length < desired && attempt < desired * 10) {
        const base = baseList[attempt % baseList.length];
        const offsetU = (((attempt + 1) * golden) % 1 - 0.5) * jitterScale;
        const offsetV = (((attempt + 1) * silver) % 1 - 0.5) * jitterScale;
        const candidate = new THREE.Vector3(base[0], base[1], base[2])
          .add(axisU.clone().multiplyScalar(offsetU))
          .add(axisV.clone().multiplyScalar(offsetV));
        expandedSeeds.push([candidate.x, candidate.y, candidate.z]);
        attempt++;
      }
    }

    while (expandedSeeds.length < desired && uniqueSeeds.length > 0) {
      expandedSeeds.push(uniqueSeeds[expandedSeeds.length % uniqueSeeds.length].slice());
    }

    const limited = expandedSeeds.slice(0, desired);
    const finalSeeds = this.offsetSeedsFromPlane(limited, geometry, normal, axisU, axisV, data);
    console.log(`✅ Sampled ${finalSeeds.length} seed points on slice plane (requested: ${desired})`);
    return finalSeeds;
  }

  sampleSeedsFromGridBounds(data, axisU, axisV, normal, count) {
    const bounds = data?.grid?.bounds;
    if (!bounds) {
      return [];
    }

    const originVec = this.planeOrigin.clone();
    const grid = data?.grid || null;

    const corners = [
      [bounds.xmin, bounds.ymin, bounds.zmin],
      [bounds.xmin, bounds.ymin, bounds.zmax],
      [bounds.xmin, bounds.ymax, bounds.zmin],
      [bounds.xmin, bounds.ymax, bounds.zmax],
      [bounds.xmax, bounds.ymin, bounds.zmin],
      [bounds.xmax, bounds.ymin, bounds.zmax],
      [bounds.xmax, bounds.ymax, bounds.zmin],
      [bounds.xmax, bounds.ymax, bounds.zmax]
    ];

    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;

    corners.forEach(corner => {
      const offset = new THREE.Vector3(corner[0], corner[1], corner[2]).sub(originVec);
      const u = offset.dot(axisU);
      const v = offset.dot(axisV);
      if (!Number.isFinite(u) || !Number.isFinite(v)) return;
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    });

    if (!Number.isFinite(minU) || !Number.isFinite(maxU) || !Number.isFinite(minV) || !Number.isFinite(maxV)) {
      return [];
    }

    const spanU = maxU - minU;
    const spanV = maxV - minV;
    if (spanU < 1e-6 || spanV < 1e-6) {
      return [];
    }

    const seeds = [];
    const dedupe = new Set();
    const golden = 0.6180339887498949;
    const silver = 0.7548776662466927;
    const jitterUBase = spanU / Math.max(Math.sqrt(count), 10);
    const jitterVBase = spanV / Math.max(Math.sqrt(count), 10);
    const epsilon = Math.max(spanU, spanV) * 1e-5;
    const maxAttempts = count * 400;

    let attempts = 0;
    while (seeds.length < count && attempts < maxAttempts) {
      const fracU = ((attempts + 0.5) * golden) % 1;
      const fracV = ((attempts + 0.25) * silver) % 1;
      const jitterU = (((attempts + 1) * silver) % 1 - 0.5) * jitterUBase;
      const jitterV = (((attempts + 1) * golden) % 1 - 0.5) * jitterVBase;

      const u = minU + fracU * spanU + jitterU;
      const v = minV + fracV * spanV + jitterV;

      const candidate = originVec.clone()
        .add(axisU.clone().multiplyScalar(u))
        .add(axisV.clone().multiplyScalar(v));

      if (grid?.bounds) {
        const b = grid.bounds;
        if (
          candidate.x < b.xmin - epsilon || candidate.x > b.xmax + epsilon ||
          candidate.y < b.ymin - epsilon || candidate.y > b.ymax + epsilon ||
          candidate.z < b.zmin - epsilon || candidate.z > b.zmax + epsilon
        ) {
          attempts++;
          continue;
        }
      }

      const key = `${Math.round(candidate.x * 1e5)}|${Math.round(candidate.y * 1e5)}|${Math.round(candidate.z * 1e5)}`;
      if (dedupe.has(key)) {
        attempts++;
        continue;
      }

      dedupe.add(key);
      seeds.push([candidate.x, candidate.y, candidate.z]);
      attempts++;
    }

    return seeds;
  }

  sampleSeedsFromSliceTriangles(geometry, targetCount) {
    if (targetCount <= 0) {
      return [];
    }

    const positionAttr = geometry.getAttribute('position');
    if (!positionAttr) {
      return [];
    }

    const indexAttr = geometry.getIndex();
    const indexArray = indexAttr ? indexAttr.array : null;

    const triangles = [];
    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();
    const edge1 = new THREE.Vector3();
    const edge2 = new THREE.Vector3();
    const cross = new THREE.Vector3();

    const pushTriangle = (ia, ib, ic) => {
      if (!Number.isFinite(ia) || !Number.isFinite(ib) || !Number.isFinite(ic)) return;
      if (ia < 0 || ib < 0 || ic < 0) return;
      if (ia >= positionAttr.count || ib >= positionAttr.count || ic >= positionAttr.count) return;

      vA.set(positionAttr.getX(ia), positionAttr.getY(ia), positionAttr.getZ(ia));
      vB.set(positionAttr.getX(ib), positionAttr.getY(ib), positionAttr.getZ(ib));
      vC.set(positionAttr.getX(ic), positionAttr.getY(ic), positionAttr.getZ(ic));

      edge1.subVectors(vB, vA);
      edge2.subVectors(vC, vA);
      cross.copy(edge1).cross(edge2);
      const area = cross.length() * 0.5;

      if (!Number.isFinite(area) || area <= 1e-12) {
        return;
      }

      triangles.push({
        a: [vA.x, vA.y, vA.z],
        b: [vB.x, vB.y, vB.z],
        c: [vC.x, vC.y, vC.z],
        area
      });
    };

    if (indexArray && indexArray.length >= 3) {
      for (let i = 0; i <= indexArray.length - 3; i += 3) {
        pushTriangle(indexArray[i], indexArray[i + 1], indexArray[i + 2]);
      }
    } else {
      for (let i = 0; i <= positionAttr.count - 3; i += 3) {
        pushTriangle(i, i + 1, i + 2);
      }
    }

    if (triangles.length === 0) {
      return [];
    }

    let totalArea = 0;
    triangles.forEach(tri => {
      totalArea += tri.area;
    });
    if (!Number.isFinite(totalArea) || totalArea <= 1e-12) {
      return [];
    }

    const seeds = [];
    const golden = 0.6180339887498949;
    const silver = 0.7548776662466927;

    let triIndex = 0;
    let cumulativeArea = triangles[0].area;

    for (let i = 0; i < targetCount; i++) {
      const target = totalArea * ((i + 0.5) / targetCount);
      while (triIndex < triangles.length - 1 && target > cumulativeArea) {
        triIndex++;
        cumulativeArea += triangles[triIndex].area;
      }

      const tri = triangles[triIndex];
      if (!tri) continue;

      const r1 = ((i + 0.5) * golden) % 1;
      const r2 = ((i + 0.5) * silver) % 1;
      const sqrtR1 = Math.sqrt(r1);
      const baryA = 1 - sqrtR1;
      const baryB = r2 * sqrtR1;
      const baryC = 1 - baryA - baryB;

      seeds.push([
        baryA * tri.a[0] + baryB * tri.b[0] + baryC * tri.c[0],
        baryA * tri.a[1] + baryB * tri.b[1] + baryC * tri.c[1],
        baryA * tri.a[2] + baryB * tri.b[2] + baryC * tri.c[2]
      ]);
    }

    return seeds;
  }

  samplePointsFallback(positionAttr, targetCount) {
    const unique = new Map();
    const tolerance = 1e-9;
    for (let i = 0; i < positionAttr.count && unique.size < targetCount; i++) {
      const x = positionAttr.getX(i);
      const y = positionAttr.getY(i);
      const z = positionAttr.getZ(i);
      const key = `${Math.round(x / tolerance) * tolerance}|${Math.round(y / tolerance) * tolerance}|${Math.round(z / tolerance) * tolerance}`;
      if (!unique.has(key)) {
        unique.set(key, [x, y, z]);
      }
    }
    const seeds = Array.from(unique.values());
    if (seeds.length === 0) {
      seeds.push([this.planeOrigin.x, this.planeOrigin.y, this.planeOrigin.z]);
    }
    return seeds.slice(0, targetCount);
  }

  offsetSeedsFromPlane(seeds, geometry, normal, axisU, axisV, data) {
    const bounds = data?.grid?.bounds || null;

    let offsetMagnitude = 1e-4;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    if (geometry.boundingBox) {
      const bb = geometry.boundingBox;
      const spanX = bb.max.x - bb.min.x;
      const spanY = bb.max.y - bb.min.y;
      const spanZ = bb.max.z - bb.min.z;
      const span = Math.max(spanX, spanY, spanZ, 1e-6);
      offsetMagnitude = Math.max(span * 1e-4, 5e-5);
    }

    const tangentialScale = offsetMagnitude * 0.5;
    const golden = 0.6180339887498949;
    const silver = 0.7548776662466927;
    const epsilon = bounds ? Math.max(
      (bounds.xmax - bounds.xmin) * 1e-6,
      (bounds.ymax - bounds.ymin) * 1e-6,
      (bounds.zmax - bounds.zmin) * 1e-6,
      1e-6
    ) : 0;

    const results = [];

    seeds.forEach((pt, idx) => {
      const base = new THREE.Vector3(pt[0], pt[1], pt[2]);
      const candidate = base.clone();

      if (axisU) {
        const jitterU = (((idx * golden) % 1) - 0.5) * tangentialScale;
        candidate.add(axisU.clone().multiplyScalar(jitterU));
      }
      if (axisV) {
        const jitterV = (((idx * silver) % 1) - 0.5) * tangentialScale;
        candidate.add(axisV.clone().multiplyScalar(jitterV));
      }

      const offsets = [0, offsetMagnitude, -offsetMagnitude, offsetMagnitude * 2, -offsetMagnitude * 2];
      let chosen = candidate.clone();

      for (const offset of offsets) {
        const test = candidate.clone().addScaledVector(normal, offset);
        if (bounds) {
          if (
            test.x < bounds.xmin - epsilon || test.x > bounds.xmax + epsilon ||
            test.y < bounds.ymin - epsilon || test.y > bounds.ymax + epsilon ||
            test.z < bounds.zmin - epsilon || test.z > bounds.zmax + epsilon
          ) {
            continue;
          }
        }
        chosen = test;
        break;
      }

      results.push([chosen.x, chosen.y, chosen.z]);
    });

    return results;
  }

  projectStreamlinesToSlicePlane() {
    const entry = this.filterManager.activeFilters.get('slice-streamlines');
    if (!entry || !Array.isArray(entry.streamlines)) {
      return;
    }

    const planeOrigin = this.planeOrigin.clone();
    const planeNormal = this.planeNormal.clone().normalize();

    const projectPoint = (point) => {
      const pointVec = new THREE.Vector3(point[0], point[1], point[2]);
      const offset = pointVec.clone().sub(planeOrigin);
      const distance = offset.dot(planeNormal);
      pointVec.sub(planeNormal.clone().multiplyScalar(distance));
      return [pointVec.x, pointVec.y, pointVec.z];
    };

    const projectedStreamlines = entry.streamlines.map(streamline => {
      const projectedPoints = Array.isArray(streamline.points)
        ? streamline.points.map(projectPoint)
        : [];
      return {
        ...streamline,
        points: projectedPoints
      };
    });

    entry.streamlines = projectedStreamlines;

    const engine = this.filterManager.engine;
    const objects = engine && engine.objects;
    const engineEntry = objects && objects.get ? objects.get('slice-streamlines') : null;
    const group = engineEntry && engineEntry.object;
    if (group && Array.isArray(group.children)) {
      let lineIndex = 0;
      group.children.forEach(child => {
        if (!child || !child.isLine) return;
        const streamline = projectedStreamlines[lineIndex] || null;
        lineIndex++;
        if (!streamline || !Array.isArray(streamline.points)) return;
        const geometry = child.geometry;
        const positionAttr = geometry?.getAttribute?.('position');
        if (!positionAttr) return;
        const count = Math.min(positionAttr.count, streamline.points.length);
        for (let i = 0; i < count; i++) {
          const projected = streamline.points[i];
          positionAttr.setXYZ(i, projected[0], projected[1], projected[2]);
        }
        positionAttr.needsUpdate = true;
        if (geometry.computeBoundingSphere) {
          geometry.computeBoundingSphere();
        }
        if (geometry.computeBoundingBox) {
          geometry.computeBoundingBox();
        }
      });
    }
  }

  // Helper: Find nearest vertex in mesh
  findNearestVertex(vertices, point) {
    let minDist = Infinity;
    let nearestIdx = 0;

    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      const dx = v[0] - point[0];
      const dy = v[1] - point[1];
      const dz = v[2] - point[2];
      const dist = dx * dx + dy * dy + dz * dz;

      if (dist < minDist) {
        minDist = dist;
        nearestIdx = i;
      }
    }

    return nearestIdx;
  }

  dispose() {
    this.disable();
  }
}

// Export to window for global access
window.InteractiveSliceWidget = InteractiveSliceWidget;
